/* eslint-disable unicorn/prefer-module */
/* eslint-disable no-console */
/* eslint-disable indent */
import { useEffect, useCallback, useState, useMemo, Fragment } from "react";
import { Dropdown } from "react-bootstrap";
import { useForm, FormProvider } from "react-hook-form";
import toast from "react-hot-toast";
import { FaBuilding, FaFileAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Collapse } from "reactstrap";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, LabelList } from "recharts";
import { getAniosCierreDisponibles, getAniosFacturaDisponibles } from "../../constants/anios";
import {
  isAdmin as checkIsAdmin,
  AGENCIA_ADLAB_PERU,
  puedeCargarOrdenMedio,
  puedeVerOrdenMedio,
} from "../../constants/permisos";
import { ROLES } from "../../constants/roles";
import { useAuthContext } from "../../context/auth";
import { useHubHeaderFilters } from "../../pages/Ventas/HubHeaderContext";
import { useVentasDataSync } from "../../pages/Ventas/VentasDataVersionContext";
import {
  getCategorias,
  getSubcategorias,
  getContratos,
  getOportunidades,
  updateContrato,
  updateOportunidad,
  getUsers,
  getIntermediarios,
  getEdificiosOperativos,
  getClientes,
  getOportunidadesBTL,
  updateOportunidadBTL,
  tieneEdificiosAsignados,
} from "../../utils";
import { getRateForOpp, getImporteEnUSD, sumarImportesPorEstatus } from "../../utils/calculateVentas";
import { cargarCobroPorVenta } from "../../utils/cobro/cargarCobroVentas";
import { resolverCobroVenta } from "../../utils/cobro/getCobroFactura";
import { getCotizacionesByCodigos } from "../../utils/cotizacionesGuardadas";
import { normalizarEmpresa } from "../../utils/empresaContacto";
import { esVentaEnPeriodoBroker } from "../../utils/esVentaEnPeriodoBroker";
import { getImporteFacturable } from "../../utils/getImporteFacturable";
import { parchearFilasAsignadas } from "../../utils/oportunidades/asignacion";
import { ESTATUS_ABIERTOS_OPORTUNIDAD, getOportunidadesPorIds } from "../../utils/oportunidades/ordenamiento";
import {
  getOrdenesMedioVigentesPorTargets,
  getVinculosVigentesDeOrdenMedio,
  getUrlOrdenMedio,
  esArchivoPendiente,
} from "../../utils/ordenesMedio";
import { resolverNombreEjecutivo } from "../../utils/resolverNombreEjecutivo";
import { useCurrency } from "../../utils/useCurrency";
import { useEmpresaProductoFilter } from "../../utils/useEmpresaProductoFilter";
import { useExchangeRates } from "../../utils/useExchangeRates";
import { AdjuntarPdfOmModal } from "../AdjuntarPdfOmModal";
import { AgenciasDropdown, matchAgenciaFilter } from "../AgenciasDropdown";
import { AsignarOportunidadModal } from "../AsignarOportunidadModal";
import { CargarOrdenMedioModal } from "../CargarOrdenMedioModal";
import { CategoriaSubcategoriaFilter } from "../CategoriaSubcategoriaFilter";
import { FlashEditModal } from "../FlashEditModal";
import KebabMenu from "../KebabMenu";
import { Loader } from "../Loader";
import { ModalAsignarEdificios } from "../ModalAsignarEdificios";
import { ComparadorModal } from "./ComparadorModal";
import "./stylesVentasDetalle.scss";
import styles from "./VentasDetalle.module.scss";

// Columnas ocultas por tramo de ancho de pantalla (decisión de Ignacio, 2026-09-30): en pantallas
// chicas la tabla de Detalle NO se comprime — se ocultan columnas de verdad (th y td, no con CSS),
// para que colSpan/rowSpan (COLUMNAS_TABLA_BASE, filasConGrupo de 4.16.10) sigan cuadrando. Mismo
// patrón que UMBRAL_MOBILE_PX de SoloEscritorio (src/components/SoloEscritorio/index.js): una única
// constante exportada, nunca repetir los números. Por debajo de 1440px se ocultan ID y CODE; por
// debajo de 1280px además INTERMEDIARIO — CLIENTE/OPORTUNIDAD/CAMPAÑA/CIERRE/FACTURA/TICKET/
// STATUS/EJECUTIVO/ACCIONES quedan siempre visibles. Solo aplica con `oportunidadesHabilitadas`
// (4.16.9) — con la feature apagada (legacy, ya no ocurre en producción) la tabla no se toca.
export const UMBRALES_COLUMNAS_DETALLE_PX = { OCULTAR_ID_CODE: 1440, OCULTAR_INTERMEDIARIO: 1280 };

const useMenorQuePx = (px) => {
  const query = `(max-width: ${px - 0.02}px)`;
  const [activo, setActivo] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (event) => setActivo(event.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return activo;
};

// Anchos por tramo — Fase 1c (2026-10-05), 2ª corrección de Cowork sobre la Fase 1b: el reparto
// anterior (máximo entre header y percentil 40 del dato truncado) dejaba a CLIENTE con espacio de
// sobra mientras CODE/AGENCIA/CAMPAÑA truncaban — medido con capturas del set real COMPLETO
// (Chile 2026: 256 sales + 17 contratos + 1 BTL, no solo 4 filas curadas). Criterio nuevo,
// SECUENCIAL por prioridad (no proporcional):
//   1) Columnas "fijas" (ID, CIERRE, FACTURA —rango de contrato más ancho real, "MAR/26 → ABR/26"
//      entre los 17 contratos—, TICKET, STATUS, TIPO, EJECUTIVO, ⋮, INVENTARIO) — intocables,
//      igual que en 1b. INVENTARIO en el tramo angosto (sin audiencia) subió de 83px a 101px: el
//      header "INVENTARIO" (~100px) nunca se había comparado contra el dato de 2 partes (83px) en
//      ESE tramo — encontrado en la verificación real de esta fase, misma clase de descuido que
//      OPORTUNIDAD/INTERMEDIARIO en la 1ª corrección.
//   2) CODE — NUNCA trunca: su ancho real máximo de TODO el histórico, "WOBLSU-v2" (9 car.,
//      99px) — 1 sola fila legacy del clonador (retirado), igual se le da el ancho completo.
//   3) CAMPAÑA (fusiona OPORTUNIDAD+CAMPAÑA) — fija en el ancho de su contenido real de HOY:
//      ningún nombre de oportunidad ni campaña real de Chile supera 17 caracteres en todo el
//      histórico ("SIN NOMBRE DBZDPH", autogenerado, 162px) — el cap de truncado a 18 nunca se
//      activa hoy.
//   4) AGENCIA — piso NO NEGOCIABLE = ancho de su propio header ("AGENCIA", 81px: NINGÚN header
//      puede partirse, es el requisito más viejo de toda la fase) + lo que sobre hasta su ancho
//      real máximo ("PUBLICIS | STARCOM", 18 car., 154px).
//   5) CLIENTE — el resto del presupuesto, hasta su ideal real máximo del año ("SECRETARÍA
//      TURISMO ARGENTINA", 28 car., 247px); si no alcanza, CLIENTE trunca con "..." + tooltip
//      ANTES que forzar a AGENCIA por debajo de su piso de header.
//
// **Ancho real de la tabla** (verificado con Playwright, NO es 0.95×viewport): el `.col-md-12`
// de Bootstrap que envuelve la tabla mete su propio padding lateral — se retiró en la Fase 1b
// (`stylesVentasDetalle.scss`, punto 3 de Ignacio). Fórmula real: tabla = 0,95×viewport − 28,5px
// (1920→1795,5px; 1440→1339,5px; 1280→1187,5px; 1200→1111,5px).
//
// **Ajuste de anchos (2026-10-06), pedido de Ignacio sobre el resultado de la Fase 1c**: INVENTARIO
// y AGENCIA tenían más aire del necesario. Dos cambios:
//   a) INVENTARIO más angosta: `INV_SLOT.audiencia` bajó de 45 a 44px (real medido para "65.799",
//      el máximo de hoy) y su `<td>`/`<th>` pasa a padding horizontal 0 (clase
//      `.celda-inventario-ajustada`, antes 8px/8px genérico) — el contenido ya reserva su propio
//      aire con el `marginRight:6px` de cada número/"|", así que ese padding era un segundo aire
//      redundante. INVENTARIO_COMPLETO: 158px → **141px**.
//   b) AGENCIA muestra solo la RED/marca (lo que va después del último "|" — "PUBLICIS | STARCOM"
//      → "STARCOM"), nunca el holding completo; sin "|" se muestra igual que siempre (DIRECTO,
//      HAVAS...). Solo esta celda cambia — filtro, orden y tooltip siguen con el valor completo
//      (`nombreAgenciaCorto`, arriba). Medido contra las 32 agencias reales de ventas Chile 2026:
//      31 necesitan ≤97px ("MINDSHARE" es la más ancha de esas, 80.9px texto + 16px padding); la
//      única excepción real es **"SMART PROCUREMENT"** (sin "|", 2 clientes, 152.4px texto+16=169px
//      — casualmente más ancha en píxeles que "PUBLICIS | STARCOM" pese a tener los mismos 18
//      caracteres, por el ancho propio de sus letras) — sigue truncando con "..." + tooltip, mismo
//      tipo de excepción aislada que ya tiene CLIENTE con sus 2 nombres más largos del año.
// El ancho que libera (a) + lo que ya tenía CAMPAÑA de sobra (su real de hoy es 160px, no 162 —
// apenas ~2px) van enteros a AGENCIA; CLIENTE no cede, se queda en su valor actual (164.8px/
// 151.4px). Resultado en `completo`: AGENCIA pasa de 81.7px a **99.1px** (cubre 31/32 agencias
// reales sin recorte) — verificado con Playwright contra el set real completo, cero recorte salvo
// el caso único y disclosado de "SMART PROCUREMENT".
//
// **Tensión histórica de CLIENTE (sin cambios en esta ronda)**: CODE (99px, nunca trunca) + CAMPAÑA
// (160px, fija) + AGENCIA (99.1px) + las 9 columnas fijas ya dejan poco presupuesto libre de los
// ~1286px totales (96%) — no alcanza para el ideal de CLIENTE (247px). A 1440px **CLIENTE entra en
// 164.8px/151.4px (sin/con comparar)** — trunca "SECRETARÍA TURISMO AR..." y similares con "..." +
// tooltip (2 de 256 ventas 2026 superan los ~17 caracteres que entran ahí). En sinIdCode (sin ID/
// CODE, 1280px) CLIENTE YA entra completo (247px). Sigue aceptado tal cual (comportamiento pedido
// explícitamente en la Fase 1c: CLIENTE prioridad, trunca con tooltip).
//
// **Bug real encontrado y corregido en la Fase 1c (histórico, no tocado en esta ronda)**:
// INVENTARIO_COMPLETO estaba en 136px, por debajo de su necesidad real — el `<td>` no sumaba su
// propio padding (8px por lado) al contenido interno, y el check automático de wraps
// (`Range.getClientRects()`, detecta 2ª LÍNEA) no veía el recorte porque era overflow HORIZONTAL en
// una sola línea ("58.903" se solapaba con "OMNICOM"), no un salto de línea. Corregido en su momento
// a 158px; esta ronda lo retoma desde ahí para angostarlo de nuevo con el padding-0 de (a).
//
// **Columna OM (Fase OM.2, 2026-10-05), recalculado con el mismo método**: angosta, a la derecha de
// STATUS. Peor caso real NO es el ícono (16px) sino el texto "x/y" de la fila de CONTRATO (10 es el
// máximo real de cuotas en un contrato hoy, verificado contra la DB: "10/10", medido en Playwright a
// 0.8rem = 34.3px) — el header de la columna ya dice "OM", así que la celda NO repite la letra (si
// dijera "OM 10/10" serían 58.4px, bastante más ancho sin aportar información nueva). +16px de
// padding (8px por lado, mismo criterio del bug de INVENTARIO de arriba) = 50.3px reales necesarios.
// Las 3 columnas ya sumaban exactamente 96.0% en cada tramo (**4% de margen libre, idéntico en los
// 3** — no es casualidad, es el margen de seguridad que ya traía la tabla) — 50.3px entra dentro de
// ese margen en los 3 tramos (completo 1339.5px→3.76% necesario, sinIdCode 1187.5px→4.24%,
// sinIdCodeIntermediario≈1111.5px→4.53%, con algo de aire extra en cada uno) → NINGUNA otra columna
// cede espacio, los valores medidos de cada una quedan intocados.
const ANCHOS_POR_TRAMO = {
  completo: {
    sinComparar: {
      id: "4.8%",
      cliente: "12.3%",
      campana: "12%",
      cierre: "5%",
      factura: "10.4%",
      ticket: "7.6%",
      status: "5.4%",
      om: "4%",
      code: "7.4%",
      tipo: "3.9%",
      inventario: "10.6%",
      intermediario: "7.4%",
      ejecutivo: "6.8%",
      acciones: "2.4%",
    },
    conComparar: {
      id: "4.8%",
      cliente: "11.3%",
      campana: "12%",
      cierre: "5%",
      factura: "10.4%",
      ticket: "7.6%",
      status: "5.4%",
      om: "4%",
      code: "7.4%",
      tipo: "3.9%",
      inventario: "10.6%",
      intermediario: "7.4%",
      ejecutivo: "6.8%",
      acciones: "2.4%",
    },
  },
  sinIdCode: {
    sinComparar: {
      cliente: "20.1%",
      campana: "13.7%",
      cierre: "5.6%",
      factura: "11.8%",
      ticket: "8.6%",
      status: "6%",
      om: "4.4%",
      tipo: "4.3%",
      inventario: "8.6%",
      intermediario: "6.9%",
      ejecutivo: "7.7%",
      acciones: "2.7%",
    },
    conComparar: {
      cliente: "19.1%",
      campana: "13.7%",
      cierre: "5.6%",
      factura: "11.8%",
      ticket: "8.6%",
      status: "6%",
      om: "4.4%",
      tipo: "4.3%",
      inventario: "8.6%",
      intermediario: "6.9%",
      ejecutivo: "7.7%",
      acciones: "2.7%",
    },
  },
  sinIdCodeIntermediario: {
    sinComparar: {
      cliente: "22.6%",
      campana: "14.6%",
      cierre: "6%",
      factura: "12.6%",
      ticket: "9.1%",
      status: "6.4%",
      om: "4.6%",
      tipo: "4.6%",
      inventario: "9.1%",
      ejecutivo: "8.2%",
      acciones: "2.8%",
    },
    conComparar: {
      cliente: "21.6%",
      campana: "14.6%",
      cierre: "6%",
      factura: "12.6%",
      ticket: "9.1%",
      status: "6.4%",
      om: "4.6%",
      tipo: "4.6%",
      inventario: "9.1%",
      ejecutivo: "8.2%",
      acciones: "2.8%",
    },
  },
};

// Slots fijos de la columna INVENTARIO (Fase 1b, requisito de Ignacio: los "|" SIEMPRE en la
// misma posición vertical, en TODAS las filas — nunca "bailan" según la cantidad de dígitos).
// Cada número vive en un span de ancho FIJO ("sin aire de sobra a la izquierda": exactamente el
// peor caso real medido contra la DB completa de Chile 2026 — edificios máx 48 → 2 díg.,
// pantallas máx 293 → 3 díg., audiencia/personas máx 65.799/56.699 → 6 car. con separador de
// miles, 1px de margen mínimo), alineado a la derecha, `tabular-nums`. El slot de ícono (subfila,
// FaBuilding) se reserva SIEMPRE, incluso cuando no hay ícono — así los números de la fila
// principal (sin ícono) y los de la subfila (con ícono) arrancan en la MISMA posición.
//
// Fase 1c (2026-10-05), 2ª corrección de Cowork: el "|" quedaba pegado al número de la izquierda
// (0px, por el alineado a la derecha sin margen) y lejos del de la derecha (todo el "aire" del
// slot siguiente, si ese número era más corto que su peor caso). Fix: cada número y cada "|"
// llevan el MISMO `marginRight` fijo (6px) — el "|" queda siempre a 6px del número que lo
// precede (que ya está pegado al borde derecho de SU slot, alineado a la derecha) y el slot
// siguiente arranca 6px después del "|", sea cual sea el ancho real de su propio número.
//
// Ajuste de anchos (2026-10-06): `audiencia` bajó de 45 a 44px — el real medido en Playwright para
// "65.799" (6 car., el máximo real de hoy) es 43.9px; 45 tenía ~1px de más. Además, el `<td>`/`<th>`
// de esta columna pasa a padding horizontal 0 (clase `.celda-inventario-ajustada`,
// stylesVentasDetalle.scss) en vez del 8px/8px genérico de toda la tabla: el contenido ya reserva
// su propio aire con el `marginRight` de 6px en cada elemento, así que el padding del `<td>` era
// aire DUPLICADO, no necesario para que nada se vea pegado al borde (el primer y último elemento
// igual quedan a 6px de sus vecinos internos, el padding genérico solo sumaba espacio extra a los
// costados). Libera ~16-17px por fila que antes no se usaban, redirigidos a AGENCIA.
const INV_GAP = 6;
const INV_SLOT = { icono: 17, edificios: 17, pantallas: 25, audiencia: 44 };
const inventarioNumeroStyle = (width) => ({
  display: "inline-block",
  flex: "0 0 auto",
  width,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
  marginRight: INV_GAP,
});
const INVENTARIO_PIPE_STYLE = { display: "inline-block", flex: "0 0 auto", marginRight: INV_GAP, color: "#adb5bd" };

// Dato faltante en un slot de INVENTARIO: punto medio gris, nunca "-" ni un 0 inventado. Fase 1c:
// subido de #ced4da a #adb5bd (casi no se leía) — mismo gris que ya usan los separadores "|".
// Módulo-scope (no depende de props/estado) para no arrastrar warnings de deps en los
// `useCallback` que lo usan.
const PUNTO_FALTANTE = <span style={{ color: "#adb5bd" }}>·</span>;
const fmtInv = (valor) => (valor === null || valor === undefined ? PUNTO_FALTANTE : valor);

// Ajuste de anchos (2026-10-06, pedido de Ignacio): AGENCIA en la columna de la tabla muestra solo
// la RED/marca (lo que va después del último "|" — el holding queda implícito), nunca el nombre
// completo del intermediario: "PUBLICIS | STARCOM" → "STARCOM", "IPG | INITIATIVE" → "INITIATIVE".
// Sin "|" (DIRECTO, HAVAS, TAP TAP, SMART PROCUREMENT...) se muestra completo, sin cambios. Es solo
// presentación de ESTA celda — el dato real (`nombreIntermediario`, el tooltip) y el filtro/orden
// por Agencia en toda la pantalla siguen usando el valor completo, intactos. Libera ancho real: de
// 32 agencias vigentes en ventas Chile 2026, 31 pasan a necesitar ≤97px (antes varias superaban
// 150px); la única que sigue truncando es "SMART PROCUREMENT" (sin "|", 169px, 2 clientes) — mismo
// tipo de excepción aislada que ya tiene CLIENTE con sus 2 nombres más largos del año.
const nombreAgenciaCorto = (nombre) => {
  if (!nombre) return nombre;
  const idx = nombre.lastIndexOf("|");
  return idx === -1 ? nombre : nombre.slice(idx + 1).trim();
};

export const VentasDetalle = ({ renderHeader, tabKey, active } = {}) => {
  // Invalidación cross-tab del hub (2026-09-22, ver VentasDataVersionContext.js y
  // docs/modulos/ventas-hub.md): `bumpVersionDatos` se llama tras cada escritura propia exitosa
  // (guardado de estatus, flash-edit, eliminar); `datosDesactualizados` se pone en `true` cuando
  // OTRA tab (la Pizarra, el CRM) escribió algo mientras Detalle estaba activo o recién al
  // volverse activo; `marcarSincronizada` avisa "ya estoy al día" tras cada `fetchData` real.
  // Fuera del hub (`active` es `undefined`) es un no-op — standalone sigue igual que siempre.
  const { bump: bumpVersionDatos, stale: datosDesactualizados, marcarSincronizada } = useVentasDataSync(!!active);
  const [categorias, setCategorias] = useState([]);
  const [deletedOpportunity, setDeletedOpportunity] = useState(null);
  const [editingOpportunity, setEditingOpportunity] = useState(null);
  // Default al abrir (decisión de Ignacio, 2026-09-21): Año cierre arranca en el año actual —
  // "si elijo 2026, muestra todo 2026" por cierre. Año factura arranca VACÍO — es un filtro de
  // PROYECCIÓN opcional (ver facturaMatch más abajo), no un default; con año actual precargado
  // ahí escondía pipeline que cierra este año pero factura el año próximo (caso real: CENCOSUD,
  // 3 oportunidades 75% cierre 09/2026, factura prevista 01/2027 — desaparecían del Detalle con
  // el default viejo, aunque el CRM sí las mostraba por no filtrar por año).
  const [filtroAnoCierre, setFiltroAnoCierre] = useState([new Date().getFullYear().toString()]);
  const [filtroAnoFactura, setFiltroAnoFactura] = useState([]);
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const [filtroSubcategoria, setFiltroSubcategoria] = useState("");
  const [subcategorias, setSubcategorias] = useState([]);
  const [filtroEjecutivo, setFiltroEjecutivo] = useState("");
  const [filtroEstatus, setFiltroEstatus] = useState([]);
  const [filtroIntermediario, setFiltroIntermediario] = useState("");
  const [filtroMes, setFiltroMes] = useState([]);
  const [intermediaryMap, setIntermediaryMap] = useState({});
  const [intermediarios, setIntermediarios] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  // Carga inicial de oportunidades/clientes/etc (fetchData) — distinta de `loading` (esa es para
  // el submit del modal de edición). Arranca en true: gatea las tarjetas/barra/tabla para no
  // mostrar "No hay ventas..."/"SIN DATOS" antes de que la carga real termine (10.177).
  const [loadingData, setLoadingData] = useState(true);
  const [oportunidades, setOportunidades] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [edificios, setEdificios] = useState([]);
  const [isBuildingsExpanded, setIsBuildingsExpanded] = useState(false);
  const [oportunidadesBTL, setOportunidadesBTL] = useState([]);
  const [contratos, setContratos] = useState([]);
  // Oportunidades (fase 2 del diseño 2026-09-25): nombre/estatus por oportunidad_id de las filas
  // cargadas, fila abierta en el modal "Asignar a oportunidad", y el filtro de texto del header.
  const [oportunidadesInfo, setOportunidadesInfo] = useState(() => new Map());
  const [asignarOportunidadFila, setAsignarOportunidadFila] = useState(null);
  const [filtroOportunidad, setFiltroOportunidad] = useState("");
  const [expandedContratos, setExpandedContratos] = useState(new Set());
  const [filtroMesCierre, setFiltroMesCierre] = useState([]);
  const [flashEditingOpportunity, setFlashEditingOpportunity] = useState(null);
  const [isFlashEditing, setIsFlashEditing] = useState(false);
  const [flashSaving, setFlashSaving] = useState(false);
  // Cuota cuyo modal de asignar edificios está abierto: { contratoId, idx } (null = cerrado).
  const [edificiosModalData, setEdificiosModalData] = useState(null);
  // Datos del modal de OM (Fase OM.1b) para la venta/cuota cuyo kebab se clickeó — null = cerrado.
  const [cargarOmData, setCargarOmData] = useState(null);
  // Mapa "sale:ID" | "contrato:ID:MES" -> OM vigente de ese target (Fase OM.1b punto 3, decide si
  // el kebab muestra "Cargar OM" o "Ver"/"Reemplazar OM").
  const [ordenesMedioVigentes, setOrdenesMedioVigentes] = useState(() => new Map());
  // OM cuyo "Adjuntar PDF" está abierto (fix 2026-10-06: la fila quedó creada pero sin archivo real
  // porque la subida falló) — null = cerrado.
  const [adjuntarPdfData, setAdjuntarPdfData] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [modoComparar, setModoComparar] = useState(false);
  const [seleccionComparar, setSeleccionComparar] = useState([]);
  const [comparadorOpen, setComparadorOpen] = useState(false);
  const [cotizacionesComparar, setCotizacionesComparar] = useState([]);

  const methods = useForm();
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = methods;
  const { pais, id, rol, agencia } = useAuthContext();
  const { formatAmount } = useCurrency();
  const isAdmin = checkIsAdmin(id);
  // Asignar edificios por mes es decisión comercial: solo gerencia/comercial o admin.
  const canAssignEdificios = isAdmin || rol === ROLES.GERENCIA || rol === ROLES.COMERCIAL;

  // Años de los filtros derivados de los datos cargados (no literales).
  // Cierre: mes_cierre de pautas + BTL, y mes_inicio/meses[] de contratos.
  // Factura: mes_factura (o `ano`) de pautas + BTL + contratos.
  const aniosCierreDisponibles = useMemo(
    () => getAniosCierreDisponibles([...oportunidades, ...oportunidadesBTL], contratos),
    [oportunidades, oportunidadesBTL, contratos]
  );
  const aniosFacturaDisponibles = useMemo(
    () => getAniosFacturaDisponibles([...oportunidades, ...oportunidadesBTL, ...contratos]),
    [oportunidades, oportunidadesBTL, contratos]
  );

  const puedeEditarOportunidad = (oportunidad) => {
    if (!oportunidad) return false;
    if (oportunidad.facturanumero) return false;
    if (oportunidad.estatus === "0%") return false;
    if (isAdmin) return true;
    if (rol !== ROLES.GERENCIA && rol !== ROLES.COMERCIAL && rol !== ROLES.BROKER) return false;
    // Defensa en profundidad: el broker solo edita ventas de SU cartera
    // (cliente.broker_id === su agencia). Refuerza el scoping de carga.
    if (rol === ROLES.BROKER) {
      const cliente = clientes.find((c) => String(c.id) === String(oportunidad.client_id));
      if (!cliente || cliente.broker_id !== parseInt(agencia, 10)) return false;
    }
    return true;
  };

  // Permiso PROPIO de OM (10.1, espejo de `puede_cargar_vinculo_orden_medio` en la base) —
  // deliberadamente SEPARADO de `puedeEditarOportunidad` de arriba (decisión de Ignacio, 3ª ronda
  // de RLS: `administrativo` carga OM pero no edita oportunidades). No depende de facturanumero ni
  // estatus — la RLS tampoco lo hace, una OM se puede cargar en cualquier momento.
  const puedeCargarOmDelCliente = (clientId) => {
    const cliente = clientes.find((c) => String(c.id) === String(clientId));
    return puedeCargarOrdenMedio({ id, rol, agencia, clienteBrokerId: cliente ? cliente.broker_id : null });
  };

  // Permiso de VER (espejo de `puede_ver_orden_medio`) — función SEPARADA de la de arriba a
  // propósito, igual que en la base (hoy mismo universo de roles, pero pueden divergir después).
  const puedeVerOmDelCliente = (clientId) => {
    const cliente = clientes.find((c) => String(c.id) === String(clientId));
    return puedeVerOrdenMedio({ id, rol, agencia, clienteBrokerId: cliente ? cliente.broker_id : null });
  };

  // Nombre de agencia/intermediario de un cliente — mismo cálculo que ya usaba la columna AGENCIA
  // (clienteIntermediarioMap/intermediaryMap), reusado acá para el header del modal de OM (punto 1).
  const nombreAgenciaDe = (clientId) => {
    const interId = clienteIntermediarioMap[clientId];
    return interId && intermediaryMap[interId] ? intermediaryMap[interId] : null;
  };

  const etiquetaMesDe = (mes) => `${mesEnLetras(mes.split("-")[0])}/${mes.split("-")[1]?.slice(-2)}`;
  const monedaDeBool = (monedaDolar) => (monedaDolar ? "USD" : null);
  const netoDeVenta = (venta) =>
    getImporteFacturable({ importe: venta.importe, descuento_acuerdo_pct: venta.descuento_acuerdo_pct });
  const netoDeCuota = (contrato, mesObj) =>
    getImporteFacturable({ importe: mesObj.importe, descuento_acuerdo_pct: contrato.descuento_acuerdo_pct });

  // Recarga el mapa de OM vigentes (Fase OM.1b) — acotado a los targets visibles hoy en la tabla,
  // nunca el dataset completo de oportunidades/contratos (10.234). Se corre al cargar y después de
  // cada guardado exitoso del modal (onSaved), para que el kebab refleje el estado nuevo sin recargar
  // la página.
  const recargarOrdenesMedioVigentes = useCallback(async () => {
    const saleIds = oportunidades.filter((o) => o.type !== "btl" && o.type !== "contrato").map((o) => o.id);
    const contratoIds = contratos.map((c) => c.id);
    if (saleIds.length === 0 && contratoIds.length === 0) {
      setOrdenesMedioVigentes(new Map());
      return;
    }
    try {
      const mapa = await getOrdenesMedioVigentesPorTargets({ saleIds, contratoIds });
      setOrdenesMedioVigentes(mapa);
    } catch (error) {
      console.error("Error cargando las Órdenes de Medio vigentes:", error);
    }
  }, [oportunidades, contratos]);

  useEffect(() => {
    recargarOrdenesMedioVigentes();
  }, [recargarOrdenesMedioVigentes]);

  // Construye el `data` del modal de OM para una venta (sin mesObj) o una cuota (con mesObj) —
  // SOLO para el modo "cargar" (sin OM vigente todavía): todo resuelto en memoria, sin queries.
  const construirCargarOmData = (oportunidad, mesObj = null) => {
    const clienteEtiqueta = oportunidad.cliente || "-";
    const agenciaEtiqueta = nombreAgenciaDe(oportunidad.client_id) || "-";

    if (!mesObj) {
      const base = {
        origenTipo: "sale",
        saleId: oportunidad.id,
        etiquetaCliente: clienteEtiqueta,
        etiquetaAgencia: agenciaEtiqueta,
        etiquetaMes: null,
        neto: netoDeVenta(oportunidad),
        moneda: monedaDeBool(oportunidad.moneda_dolar),
      };
      return {
        pais,
        modo: "cargar",
        omVigente: null,
        basesFijas: [base],
        siblings: [],
        etiqueta: clienteEtiqueta,
        oportunidadesTodas: oportunidades,
        contratosTodos: contratos,
      };
    }

    const baseActual = {
      origenTipo: "contrato_mes",
      contratoId: oportunidad.id,
      mes: mesObj.mes,
      etiquetaCliente: clienteEtiqueta,
      etiquetaAgencia: agenciaEtiqueta,
      etiquetaMes: etiquetaMesDe(mesObj.mes),
      neto: netoDeCuota(oportunidad, mesObj),
      moneda: monedaDeBool(oportunidad.moneda_dolar),
    };
    const siblings = (oportunidad.meses || [])
      .filter((m) => m.mes !== mesObj.mes)
      .filter((m) => !ordenesMedioVigentes.has(`contrato:${oportunidad.id}:${m.mes}`))
      .map((m) => ({
        origenTipo: "contrato_mes",
        contratoId: oportunidad.id,
        mes: m.mes,
        etiquetaCliente: clienteEtiqueta,
        etiquetaAgencia: agenciaEtiqueta,
        etiquetaMes: etiquetaMesDe(m.mes),
        neto: netoDeCuota(oportunidad, m),
        moneda: monedaDeBool(oportunidad.moneda_dolar),
      }));

    return {
      pais,
      modo: "cargar",
      omVigente: null,
      basesFijas: [baseActual],
      siblings,
      etiqueta: `${clienteEtiqueta} — ${baseActual.etiquetaMes}`,
      oportunidadesTodas: oportunidades,
      contratosTodos: contratos,
    };
  };

  // Resuelve un vínculo VIEJO (de la OM a reemplazar) contra lo ya cargado en memoria — degrada con
  // gracia (etiqueta genérica + neto 0) si esa venta/cuota no está en el dataset visible hoy, nunca
  // rompe (10.177): los ids igual viajan correctos a la RPC, solo la ETIQUETA puede quedar pobre.
  const resolverVinculoViejoOm = (v) => {
    if (v.origen_tipo === "sale") {
      const venta = oportunidades.find((o) => String(o.id) === String(v.sale_id));
      return {
        origenTipo: "sale",
        saleId: v.sale_id,
        etiquetaCliente: venta?.cliente || `Venta #${v.sale_id}`,
        etiquetaAgencia: venta ? nombreAgenciaDe(venta.client_id) || "-" : "-",
        etiquetaMes: null,
        neto: venta ? netoDeVenta(venta) : 0,
        moneda: venta ? monedaDeBool(venta.moneda_dolar) : null,
      };
    }
    const contrato = contratos.find((c) => String(c.id) === String(v.contrato_id));
    const mesObj = contrato?.meses?.find((m) => m.mes === v.mes);
    return {
      origenTipo: "contrato_mes",
      contratoId: v.contrato_id,
      mes: v.mes,
      etiquetaCliente: contrato?.cliente || `Contrato #${v.contrato_id}`,
      etiquetaAgencia: contrato ? nombreAgenciaDe(contrato.client_id) || "-" : "-",
      etiquetaMes: etiquetaMesDe(v.mes),
      neto: contrato && mesObj ? netoDeCuota(contrato, mesObj) : 0,
      moneda: contrato ? monedaDeBool(contrato.moneda_dolar) : null,
    };
  };

  // Abre el modal en modo "Reemplazar OM" (punto 3) — necesita traer TODOS los vínculos vigentes de
  // la OM vieja (no solo el target clickeado: reemplazar apaga TODOS, spec punto 3), por eso es
  // async. `contratoIdDeOrigen` (si el click vino de una cuota) habilita los siblings del punto 4.
  const abrirReemplazarOm = async (omVigenteInfo, contratoIdDeOrigen = null) => {
    try {
      const vinculosViejos = await getVinculosVigentesDeOrdenMedio(omVigenteInfo.omId);
      const basesFijas = vinculosViejos.map(resolverVinculoViejoOm);
      let siblings = [];
      if (contratoIdDeOrigen != null) {
        const contrato = contratos.find((c) => String(c.id) === String(contratoIdDeOrigen));
        if (contrato) {
          const mesesYaIncluidos = new Set(
            basesFijas
              .filter((b) => b.origenTipo === "contrato_mes" && String(b.contratoId) === String(contratoIdDeOrigen))
              .map((b) => b.mes)
          );
          siblings = (contrato.meses || [])
            .filter((m) => !mesesYaIncluidos.has(m.mes))
            .filter((m) => !ordenesMedioVigentes.has(`contrato:${contrato.id}:${m.mes}`))
            .map((m) => ({
              origenTipo: "contrato_mes",
              contratoId: contrato.id,
              mes: m.mes,
              etiquetaCliente: contrato.cliente || "-",
              etiquetaAgencia: nombreAgenciaDe(contrato.client_id) || "-",
              etiquetaMes: etiquetaMesDe(m.mes),
              neto: netoDeCuota(contrato, m),
              moneda: monedaDeBool(contrato.moneda_dolar),
            }));
        }
      }
      setCargarOmData({
        pais,
        modo: "reemplazar",
        omVigente: omVigenteInfo,
        basesFijas,
        siblings,
        etiqueta: `Reemplazo de OM ${omVigenteInfo.numeroOm}`,
        oportunidadesTodas: oportunidades,
        contratosTodos: contratos,
      });
    } catch (error) {
      console.error("Error preparando el reemplazo de OM:", error);
      toast.error("No se pudo cargar los datos de la OM a reemplazar");
    }
  };

  // "Ver OM" (punto 3) — URL firmada, nueva pestaña. Sin modal: es solo abrir el PDF.
  const verOm = async (omVigenteInfo) => {
    try {
      const url = await getUrlOrdenMedio(omVigenteInfo.archivoPath);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Error abriendo el PDF de la OM:", error);
      toast.error("No se pudo abrir el PDF de la OM");
    }
  };

  // Columna "OM" (Fase OM.2, spec original de la sesión) — angosta, a la derecha de STATUS. BTL
  // nunca lleva OM (fuera del modelo, 10.186). Fila de CONTRATO (mesObj null): resumen "OM x/y"
  // (cuotas con OM vigente sobre el total), texto plano, sin ícono ni click propio — cada CUOTA
  // tiene su propio ícono. Venta normal y cada cuota: ícono de documento — verde + click abre el
  // PDF si hay OM vigente (gateado por VER); gris + click abre "Cargar OM" si no hay y el usuario
  // puede cargar; sin permiso, el ícono queda estático (sin cursor ni title de acción). Sin
  // rojo/alarma por falta de OM a propósito (no todas las ventas tienen OM) — nunca `#dc3545`.
  const renderOmCelda = (oportunidad, mesObj = null) => {
    if (oportunidad.type === "btl") return "-";

    if (!mesObj && oportunidad.type === "contrato") {
      const meses = oportunidad.meses || [];
      const conOm = meses.filter((m) => ordenesMedioVigentes.has(`contrato:${oportunidad.id}:${m.mes}`)).length;
      return (
        <span style={{ fontSize: "0.8rem", color: "#495057" }} title={`${conOm} de ${meses.length} cuotas con OM`}>
          {conOm}/{meses.length}
        </span>
      );
    }

    const key = mesObj ? `contrato:${oportunidad.id}:${mesObj.mes}` : `sale:${oportunidad.id}`;
    const omInfo = ordenesMedioVigentes.get(key);

    if (omInfo && esArchivoPendiente(omInfo.archivoPath)) {
      // Fix 2026-10-06: la fila se creó pero el PDF nunca se subió (ver header de ordenesMedio.js) —
      // ámbar, nunca rojo (sigue sin ser una alarma, es un pendiente de carga). Cualquiera con
      // permiso de CARGAR puede completar el PDF (mismo gate que "Cargar OM"/"Reemplazar OM").
      const puedeCargar = puedeCargarOmDelCliente(oportunidad.client_id);
      return (
        <FaFileAlt
          title={puedeCargar ? `OM ${omInfo.numeroOm} — falta adjuntar el PDF` : undefined}
          onClick={
            puedeCargar
              ? (e) => {
                  e.stopPropagation();
                  setAdjuntarPdfData(omInfo);
                }
              : undefined
          }
          style={{ color: "#fd7e14", cursor: puedeCargar ? "pointer" : "default" }}
        />
      );
    }

    if (omInfo) {
      const puedeVer = puedeVerOmDelCliente(oportunidad.client_id);
      return (
        <FaFileAlt
          title={puedeVer ? `OM ${omInfo.numeroOm} — ${omInfo.fechaOm}` : undefined}
          onClick={
            puedeVer
              ? (e) => {
                  e.stopPropagation();
                  verOm(omInfo);
                }
              : undefined
          }
          style={{ color: "#198754", cursor: puedeVer ? "pointer" : "default" }}
        />
      );
    }

    const puedeCargar = puedeCargarOmDelCliente(oportunidad.client_id);
    return (
      <FaFileAlt
        title={puedeCargar ? "Cargar OM" : undefined}
        onClick={
          puedeCargar
            ? (e) => {
                e.stopPropagation();
                setCargarOmData(construirCargarOmData(oportunidad, mesObj));
              }
            : undefined
        }
        style={{ color: "#adb5bd", cursor: puedeCargar ? "pointer" : "default" }}
      />
    );
  };

  const { getDolarValue, dolarFallback, loaded: ratesLoaded } = useExchangeRates(pais);
  const fallbackRate = dolarFallback?.[pais] || 1;

  const [viewInUSD, setViewInUSD] = useState(false);

  const localString = useCallback(
    (paisArg) =>
      ({
        Chile: "es-CL",
        Perú: "es-PE",
        Uruguay: "es-UY",
        Region: "es-US",
      })[paisArg] || "es-UY",
    []
  );

  // Conversión USD → moneda local con rate histórico por fecha de la opp.
  // Si moneda_dolar=false, retorna importe sin tocar.
  const getImporteLocal = useCallback(
    (op) => {
      // LISTA (7.22): VentasDetalle es ficha/detalle de la venta → muestra el importe de lista en ambas
      // monedas (la rama USD `getImporteEnUSD` ya es lista); no aplica el descuento de acuerdo OMNET.
      const importe = op.type === "btl" ? op.total_investment || 0 : parseFloat(op.importe) || 0;
      if (!op.moneda_dolar) return importe;
      const rate = getRateForOpp(op, getDolarValue, fallbackRate);
      return importe * rate;
    },
    [getDolarValue, fallbackRate]
  );

  // Formato unificado vía el central useCurrency (10.1/10.27) — antes reimplementaba
  // símbolo/locale/decimales a mano, con USD a 2 decimales (pedido de Ignacio, 2026-09-21:
  // los montos en dólares se muestran sin decimales, igual que ya hacía formatAmount). Perú
  // local (PEN) preserva 2 decimales como siempre tuvo esta pantalla — formatAmount da PEN a 0
  // por defecto, pero cambiarlo es un pedido aparte (fuera de alcance, ver docs/modulos/ventas-detalle.md).
  const formatMoneda = useCallback(
    (valor, monedaDolar = false) => {
      if (valor === null || valor === undefined || isNaN(valor)) return "-";
      if (monedaDolar) return formatAmount(valor, "USD");
      if (pais === "Perú") return formatAmount(valor, "PEN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return formatAmount(valor, "CLP");
    },
    [pais, formatAmount]
  );

  const mesEnLetras = useCallback((mes) => {
    const mesesEnLetras = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];
    return mesesEnLetras[parseInt(mes) - 1] || "-";
  }, []);

  // Columna TIPO (fase 1, 2026-10-04): vuelve la columna que estaba comentada entre CODE e
  // INTERMEDIARIO, sin los badges de meses/AR que tenía antes (decisión de Ignacio: solo la
  // letra). BTL se decide por `type`; el resto (sales Y contratos) por `tipo` — mismo campo en
  // ambas tablas, sin caso especial para contratos.
  const tipoVenta = useCallback((oportunidad) => {
    if (oportunidad.type === "btl") return "BTL";
    if (oportunidad.tipo === "p") return "P";
    if (oportunidad.tipo === "d" || oportunidad.tipo == null) return "D";
    if (oportunidad.tipo === "m") return "M";
    return oportunidad.tipo || "-";
  }, []);

  // Columna INVENTARIO (Fase 1b, 2026-10-04): "edificios | pantallas | audiencia" en slots FIJOS
  // (INV_SLOT, ver arriba) — nunca un string con separadores "bailando" según la cantidad de
  // dígitos (pedido explícito de Ignacio tras rechazar visualmente la Fase 1). Dato faltante
  // (null/undefined) → punto medio "·" gris tenue en su slot (pedido de Ignacio, 2ª corrección —
  // antes era "-"), nunca un 0 inventado. Audiencia solo en el tramo completo (>=1440px,
  // `conAudiencia`) — confirmado que los NULL de `audiencia_total` son un problema de datos, no de
  // diseño (investigación en el reporte de cierre de esta fase).
  const fmtInvMiles = useCallback(
    (valor) =>
      valor === null || valor === undefined ? PUNTO_FALTANTE : Number(valor).toLocaleString(localString(pais)),
    [pais, localString]
  );

  // `iconoSlot`: nodo del ícono de edificio (subfila, FaBuilding) o `null`. El slot se reserva
  // SIEMPRE (ancho fijo, vacío si no hay ícono) — así los números de la fila principal (sin
  // ícono) y los de la subfila (con ícono) arrancan en la MISMA posición horizontal.
  const renderInventario = useCallback(
    (edificios, pantallas, audiencia, { conAudiencia = false, iconoSlot = undefined } = {}) => (
      <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
        {iconoSlot !== undefined && (
          <span
            style={{
              display: "inline-flex",
              flex: "0 0 auto",
              width: INV_SLOT.icono,
              marginRight: INV_GAP,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {iconoSlot}
          </span>
        )}
        <span style={inventarioNumeroStyle(INV_SLOT.edificios)}>{fmtInv(edificios)}</span>
        <span style={INVENTARIO_PIPE_STYLE}>|</span>
        <span style={inventarioNumeroStyle(INV_SLOT.pantallas)}>{fmtInv(pantallas)}</span>
        {conAudiencia && (
          <>
            {/* Ajuste (2026-10-06): cuando FALTA la audiencia de esta fila (no el tramo de ancho —
                ese es `conAudiencia`), ni el 2º "|" ni el "·" se muestran — el slot queda vacío
                (`visibility:hidden`, nunca `display:none`: reserva el mismo ancho que si tuviera
                dato, así edificios/pantallas de TODAS las filas siguen centrados en la misma
                posición, con o sin audiencia real). */}
            <span style={{ ...INVENTARIO_PIPE_STYLE, visibility: audiencia == null ? "hidden" : "visible" }}>|</span>
            <span style={{ ...inventarioNumeroStyle(INV_SLOT.audiencia), marginRight: 0 }}>
              {audiencia == null ? "" : fmtInvMiles(audiencia)}
            </span>
          </>
        )}
      </span>
    ),
    [fmtInvMiles]
  );

  // `iconoSlot: null` (nunca omitido): reserva el mismo slot vacío que usa la subfila, así los
  // números de venta/BTL arrancan en la MISMA posición horizontal que los de la subfila de cuota
  // (con o sin ícono) — requisito de alineación en TODAS las filas, no solo entre cuotas.
  const inventarioVenta = useCallback(
    (oportunidad, conAudiencia) =>
      renderInventario(oportunidad.edificiostotales, oportunidad.pantallastotales, oportunidad.audiencia_total, {
        conAudiencia,
        iconoSlot: null,
      }),
    [renderInventario]
  );

  // sales_contratos NO tiene edificios/pantallas totales reales a nivel contrato (solo el
  // snapshot de cotización `edificios_referencia_totales`/`pantallas_referencia_totales`, un dato
  // distinto de lo realmente asignado por cuota) — decisión de Ignacio: la fila principal del
  // contrato muestra el texto "Contrato", los números reales viven solo en las subfilas por
  // cuota (ver JSX de la subfila, más abajo).
  const INVENTARIO_CONTRATO = <span style={{ fontStyle: "italic", color: "#6c757d" }}>Contrato</span>;

  // sales_btl: `buildings` (jsonb) solo trae {id, importe, comision} por edificio — ni pantallas
  // ni audiencia. Edificios = cantidad real de edificios del array (no inventado); pantallas y
  // audiencia siempre "-" (decisión de Ignacio, universo real = 1 fila en producción).
  const inventarioBTL = useCallback(
    (oportunidad, conAudiencia) =>
      renderInventario(Array.isArray(oportunidad.buildings) ? oportunidad.buildings.length : null, null, null, {
        conAudiencia,
        iconoSlot: null,
      }),
    [renderInventario]
  );

  const isEditableByDate = (createdAt) => {
    const today = new Date("2025-04-19");
    const createdDate = new Date(createdAt);
    return !isNaN(createdDate) && createdDate >= today;
  };

  const navigate = useNavigate();

  const handleMesChange = useCallback((e) => {
    const selectedMonths = Array.from(
      e.target.closest(".dropdown-menu").querySelectorAll("input[type=checkbox]:checked"),
      (checkbox) => checkbox.value
    );
    setFiltroMes(selectedMonths);
  }, []);

  const handleMesCierreChange = useCallback((e) => {
    const selectedMonths = Array.from(
      e.target.closest(".dropdown-menu").querySelectorAll("input[type=checkbox]:checked"),
      (checkbox) => checkbox.value
    );
    setFiltroMesCierre(selectedMonths);
  }, []);

  const renderMesOptions = (filtro, onChangeHandler) =>
    ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"].map((mes, index) => (
      <div key={index} className="form-check left-aligned-checkbox">
        <input
          className="form-check-input"
          type="checkbox"
          id={`mes${filtro === filtroMes ? "factura" : "cierre"}${index}`}
          value={index < 9 ? `0${index + 1}` : `${index + 1}`}
          checked={filtro.includes(index < 9 ? `0${index + 1}` : `${index + 1}`)}
          onChange={onChangeHandler}
        />
        <label className="form-check-label" htmlFor={`mes${filtro === filtroMes ? "factura" : "cierre"}${index}`}>
          {mes}
        </label>
      </div>
    ));

  // Filtro bidireccional empresa↔producto — lógica centralizada en useEmpresaProductoFilter.
  // En este componente el state de empresa se llama `filtroCliente` (alias) por compatibilidad
  // con las comparaciones de filtrado de tabla existentes (op.cliente / cliente.empresa).
  const clientesPais = useMemo(() => clientes.filter((cliente) => cliente.pais === pais), [clientes, pais]);
  const {
    filtroEmpresa: filtroCliente,
    filtroProducto,
    productosDeEmpresa,
    empresasDeProducto,
    empresaDisabled,
    productoDisabled,
    onChangeEmpresa,
    onChangeProducto,
  } = useEmpresaProductoFilter(clientesPais);

  const renderClientesOptions = () =>
    empresasDeProducto.map((empresa, index) => (
      <option key={index} value={empresa}>
        {empresa}
      </option>
    ));

  const renderProductosOptions = () =>
    productosDeEmpresa.map((producto, index) => (
      <option key={index} value={producto}>
        {producto}
      </option>
    ));

  const clienteCategoriaIdMap = useMemo(() => {
    const map = {};
    clientes.forEach((c) => {
      map[c.id] = { categoriaId: c.categoria, subcategoriaId: c.subcategoria_id };
    });
    return map;
  }, [clientes]);

  const clienteIntermediarioMap = useMemo(() => {
    const map = {};
    clientes.forEach((cliente) => {
      map[cliente.id] = cliente.agencia;
    });
    return map;
  }, [clientes]);

  const handleEstatusChange = (e) => {
    const value = e.target.value;
    setFiltroEstatus((prev) => (prev.includes(value) ? prev.filter((estatus) => estatus !== value) : [...prev, value]));
  };

  const renderEstatusOptions = () =>
    Object.entries({ "100%": 0, "90%": 1, "75%": 2, "50%": 3, "0%": 4 }).map(([estatus, _], index) => (
      <div key={index} className="form-check left-aligned-checkbox">
        <input
          className="form-check-input"
          type="checkbox"
          id={`estatus${index}`}
          value={estatus}
          checked={filtroEstatus.includes(estatus)}
          onChange={handleEstatusChange}
        />
        <label className="form-check-label" htmlFor={`estatus${index}`}>
          {estatus}
        </label>
      </div>
    ));

  const handleEdit = (oportunidad) => {
    if (!puedeEditarOportunidad(oportunidad)) {
      toast.error("No tienes permiso para editar esta oportunidad.");
      return;
    }
    if (oportunidad.type === "btl") {
      toast.info("La edición de oportunidades BTL está en desarrollo.");
      return;
    }
    if (pais === "Perú" || pais === "Chile") {
      if (isEditableByDate(oportunidad.created_at)) {
        navigate(`/admin/online-cotizador/editar/${oportunidad.id}`, { state: { oportunidad } });
      } else {
        setEditingOpportunity(oportunidad);
        setIsEditing(true);
      }
    } else {
      console.warn("Edición no soportada para el país:", pais);
      toast.info("Edición no disponible para este país.");
    }
  };

  const onSubmitEdit = async (data) => {
    try {
      setLoading(true);
      const updatedOportunidad = {
        estatus: data.estatus,
        mes_cierre: data.mes_cierre,
        mes: data.mes,
        updated_at: new Date().toISOString(),
      };
      await updateOportunidad({ id: editingOpportunity.id, oportunidad: updatedOportunidad });
      bumpVersionDatos();
      toast.success("Oportunidad actualizada correctamente");
      setOportunidades((prev) =>
        prev.map((op) => (op.id === editingOpportunity.id ? { ...op, ...updatedOportunidad } : op))
      );
      setIsEditing(false);
      setEditingOpportunity(null);
    } catch (error) {
      console.error("Error updating oportunidad:", error);
      toast.error("Error al actualizar la oportunidad");
    } finally {
      setLoading(false);
    }
  };

  const months = [
    { value: "01", label: "ENE" },
    { value: "02", label: "FEB" },
    { value: "03", label: "MAR" },
    { value: "04", label: "ABR" },
    { value: "05", label: "MAY" },
    { value: "06", label: "JUN" },
    { value: "07", label: "JUL" },
    { value: "08", label: "AGO" },
    { value: "09", label: "SEP" },
    { value: "10", label: "OCT" },
    { value: "11", label: "NOV" },
    { value: "12", label: "DIC" },
  ];

  const handleDelete = async (id, type) => {
    try {
      setLoading(true);
      if (type === "btl") {
        await updateOportunidadBTL({ id, oportunidad: { eliminada: true } });
        setOportunidadesBTL((prev) => prev.map((op) => (op.full_id === id ? { ...op, eliminada: true } : op)));
      } else if (type === "contrato") {
        await updateContrato({ id, contrato: { eliminada: true } });
        setContratos((prev) => prev.map((op) => (op.id === id ? { ...op, eliminada: true } : op)));
      } else {
        await updateOportunidad({ id, oportunidad: { eliminada: true } });
        setOportunidades((prev) => prev.map((op) => (op.id === id ? { ...op, eliminada: true } : op)));
      }
      bumpVersionDatos();
      toast.success(
        type === "contrato" ? "El contrato se eliminó correctamente" : "La oportunidad se eliminó correctamente"
      );
      setShowConfirmationModal(false);
      setDeletedOpportunity(null);
    } catch (error) {
      console.error("Error deleting oportunidad:", error);
      toast.error("Hubo un error al eliminar, prueba de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmationModal = (oportunidad) => {
    setShowConfirmationModal(true);
    setDeletedOpportunity(oportunidad);
  };

  useEffect(() => {
    const fetchIntermediaries = async () => {
      try {
        const fetchedIntermediarios = await getIntermediarios({ pais });
        const map = fetchedIntermediarios.reduce((acc, inter) => {
          acc[inter.id] = inter.nombre;
          return acc;
        }, {});
        setIntermediaryMap(map);
        setIntermediarios(fetchedIntermediarios);
      } catch (error) {
        console.error("Error fetching intermediaries:", error);
      }
    };
    fetchIntermediaries();
  }, [pais]);

  useEffect(() => {
    if (editingOpportunity) {
      methods.reset({
        estatus: editingOpportunity.estatus || "",
        mes_cierre: editingOpportunity.mes_cierre || "01",
        mes: editingOpportunity.mes || "01",
      });
    }
  }, [editingOpportunity, methods]);

  const fetchData = useCallback(async () => {
    try {
      const normalizeMonthLocal = (month, ano, created_at, fallbackMonth = null) => {
        if (!month && !fallbackMonth) return null;
        const monthValue = month || fallbackMonth;
        const monthStr = monthValue.toString().trim();
        if (/^\d{2}-\d{4}$/.test(monthStr)) return monthStr;
        if (/^\d{4}-\d{2}-\d{2}$/.test(monthStr)) {
          return `${monthStr.slice(5, 7)}-${monthStr.slice(0, 4)}`;
        }
        const monthNum = parseInt(monthStr, 10);
        if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) return null;
        const paddedMonth = monthNum.toString().padStart(2, "0");
        const year = ano ? ano.toString() : new Date(created_at).getFullYear().toString();
        return `${paddedMonth}-${year}`;
      };

      // 1. CARGAR CLIENTES PRIMERO (crucial)
      const fetchedClientes = await getClientes({ pais });
      const fetchedUsers = getUsers();
      const newOportunidadesRaw = getOportunidades({ pais });
      const newOportunidadesBTLRaw = getOportunidadesBTL({ pais: pais });
      const newContratosRaw = getContratos({ pais });
      const fetchedIntermediariosVentas = getIntermediarios({ pais });

      const ventasResults = await Promise.all([
        fetchedClientes,
        fetchedUsers,
        newOportunidadesRaw,
        newOportunidadesBTLRaw,
        newContratosRaw,
        fetchedIntermediariosVentas,
      ]);
      const usersData = ventasResults[1];
      // Intermediarios cargados acá (no del useEffect separado) para resolver el nombre del
      // broker en la columna EJECUTIVO sin race condition de carga.
      const intermediariosData = ventasResults[5] || [];
      // Rol BROKER sin scoping de LECTURA: ve todo el movimiento comercial de su país,
      // como comercial/gerencia. La edición sigue acotada a AdLab en `puedeEditarOportunidad`.
      const clientesData = ventasResults[0];
      const oportunidadesData = ventasResults[2];
      const btlData = ventasResults[3];
      const contratosData = ventasResults[4];

      setClientes(clientesData || []);
      setUsers(usersData || []);

      // 2. AHORA SÍ procesar oportunidades normales
      const mappedOportunidades = oportunidadesData.map((oportunidad) => {
        const ejecutivoName =
          resolverNombreEjecutivo(
            oportunidad.ejecutivo,
            usersData,
            intermediariosData,
            oportunidad.client_id,
            clientesData,
            oportunidad.created_at,
            oportunidad.estatus,
            oportunidad.fecha_cierre,
            oportunidad.excluir_broker
          ) ?? (typeof oportunidad.ejecutivo === "string" ? oportunidad.ejecutivo.split(" ")[0] : "Sin asignar");

        const normalizedMesCierre = normalizeMonthLocal(
          oportunidad.mes_cierre,
          oportunidad.ano || oportunidad.ano_cierre,
          oportunidad.created_at,
          oportunidad.mes_cierre_old || oportunidad.mes_old
        );

        const normalizedMesFactura = normalizeMonthLocal(
          oportunidad.mes_factura,
          oportunidad.ano || oportunidad.ano_cierre,
          oportunidad.created_at,
          oportunidad.mes_old || oportunidad.mes_cierre_old
        );

        const anoOportunidad =
          normalizedMesCierre?.split("-")?.[1] ||
          normalizedMesFactura?.split("-")?.[1] ||
          new Date(oportunidad.created_at).getFullYear().toString();

        return {
          ...oportunidad,
          ejecutivoId: oportunidad.ejecutivo,
          ejecutivo: ejecutivoName,
          mes_cierre: normalizedMesCierre,
          mes_factura: normalizedMesFactura,
          estatus: oportunidad.estatus || (oportunidad.status ? `${oportunidad.status}%` : "0%"),
          ano: anoOportunidad,
        };
      });
      // Recaudada por la VERDAD de cobro real (resolverCobroVenta, 10.264), NO el facturafechacobro
      // crudo de la venta. Solo display (la "Fecha de Cobro" del detalle) — NO se pisa el crudo.
      // Overlay SOLO sobre sales — contratos/BTL se dejan como están (su fecha vive por mes/sub-fila,
      // fuera de alcance acá).
      const cobroPorVenta = await cargarCobroPorVenta(pais).catch(() => new Map());
      mappedOportunidades.forEach((op) => {
        if (op.type === "contrato" || op.type === "btl" || op.isBTL) return;
        op._cobroReal = resolverCobroVenta(
          { _key: `sales:${op.id}:`, facturafechacobro: op.facturafechacobro },
          cobroPorVenta
        );
      });
      setOportunidades(mappedOportunidades);

      // 3. AHORA SÍ procesar BTL (con clientes ya cargados)
      const mappedBTL = btlData
        .filter((btl) => !btl.eliminada && btl.pais === pais)
        .map((btl) => {
          const clienteEmpresa =
            clientesData.find((c) => String(c.id) === String(btl.client_id))?.empresa || "Cliente no encontrado";
          const ejecutivoName =
            resolverNombreEjecutivo(
              btl.executive_responsible || btl.created_by,
              usersData,
              intermediariosData,
              btl.client_id,
              clientesData,
              btl.created_at,
              btl.status ? `${btl.status}%` : "75%",
              btl.fecha_cierre,
              btl.excluir_broker
            ) ?? "Sin asignar";
          return {
            ...btl,
            id: btl.numeric_id.toString(),
            full_id: btl.id,
            type: "btl",
            cliente: clienteEmpresa,
            ejecutivo: ejecutivoName,
            ejecutivoId: btl.executive_responsible || btl.created_by,
            mes_cierre: btl.mes_cierre || null,
            mes_factura: btl.mes_factura || null,
            estatus: btl.status ? `${btl.status}%` : "75%",
            ano: btl.mes_cierre?.split("-")[1] || "2025",
            facturanumero: btl.facturanumero,
            facturafecha: btl.facturafecha,
          };
        });

      setOportunidadesBTL(mappedBTL);

      // 4. Procesar contratos multi-mes
      const mappedContratos = (contratosData || [])
        .filter((c) => !c.eliminada && c.pais === pais)
        .map((c) => {
          const clienteEmpresa =
            clientesData.find((cl) => String(cl.id) === String(c.client_id))?.empresa || c.cliente || "Sin cliente";
          const ejecutivoName =
            resolverNombreEjecutivo(
              c.ejecutivo,
              usersData,
              intermediariosData,
              c.client_id,
              clientesData,
              c.created_at,
              c.estatus,
              c.fecha_cierre,
              c.excluir_broker
            ) ?? "Sin asignar";
          // Si alguna cuota del contrato ya tiene factura emitida, el contrato
          // queda congelado (mismo criterio que sales.facturanumero).
          const tieneCuotaFacturada = (c.meses || []).some((m) => m?.facturanumero);
          return {
            ...c,
            type: "contrato",
            cliente: clienteEmpresa,
            ejecutivo: ejecutivoName,
            ejecutivoId: c.ejecutivo,
            importe: c.importe_total,
            mes_factura: c.mes_inicio,
            estatus: c.estatus || "50%",
            ano: c.ano?.toString() || c.mes_inicio?.split("-")[1] || "2026",
            facturanumero: tieneCuotaFacturada ? "CONTRATO_FACTURADO" : null,
          };
        });
      setContratos(mappedContratos);

      // Nombres de las oportunidades de las filas (sales y contratos). Query aparte y no embed: la
      // carga pasa por el wrapper supabase.select (10.8), que no admite embeds. Sin la migración de
      // la fase 1 ninguna fila trae oportunidad_id y no se consulta nada.
      const idsOportunidad = [...mappedOportunidades, ...mappedContratos]
        .map((o) => o.oportunidad_id)
        .filter((id) => id != null);
      if (idsOportunidad.length > 0) {
        const { data: infoOportunidades, error: errorOportunidades } = await getOportunidadesPorIds(idsOportunidad);
        if (errorOportunidades) {
          toast.error(`No se pudieron cargar los nombres de las oportunidades: ${errorOportunidades}`);
        } else {
          setOportunidadesInfo(infoOportunidades);
        }
      }

      // resto de cargas (categorías, edificios, etc.)
      const newCategorias = await getCategorias();
      setCategorias(newCategorias);
      const newSubcategorias = await getSubcategorias();
      setSubcategorias(newSubcategorias || []);
      const fetchedEdificios = await getEdificiosOperativos({ pais });
      setEdificios(fetchedEdificios || []);
      marcarSincronizada();
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoadingData(false);
    }
  }, [pais, marcarSincronizada]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Invalidación cross-tab (2026-09-22): recarga SILENCIOSA (sin loader — la data vieja se sigue
  // mostrando hasta que la nueva está lista) cuando otra tab del hub escribió algo y Detalle está
  // activo. `useVentasDataSync` ya garantiza que esto nunca corre con la tab oculta.
  useEffect(() => {
    if (!datosDesactualizados) return;
    fetchData();
  }, [datosDesactualizados, fetchData]);

  // Flash form state ahora se maneja dentro de FlashEditModal

  const handleAnoCierreChange = (e) => {
    const value = e.target.value;
    setFiltroAnoCierre((prev) => (prev.includes(value) ? prev.filter((ano) => ano !== value) : [...prev, value]));
  };

  const handleAnoFacturaChange = (e) => {
    const value = e.target.value;
    setFiltroAnoFactura((prev) => (prev.includes(value) ? prev.filter((ano) => ano !== value) : [...prev, value]));
  };

  const handleFlashEdit = (oportunidad) => {
    if (!puedeEditarOportunidad(oportunidad)) {
      toast.error("No tienes permiso para editar esta oportunidad.");
      return;
    }
    if (oportunidad.type === "btl") {
      toast.info("La edición de oportunidades BTL está en desarrollo.");
      return;
    }
    // Contratos: solo editar status y cierre (no factura, ya que es un rango)
    const cliente = clientes.find((c) => c.id === oportunidad.client_id);
    setFlashEditingOpportunity({ ...oportunidad, clienteNombre: cliente?.nombre || cliente?.empresa || "" });
    setIsFlashEditing(true);
  };

  const onSubmitFlashEdit = async (updatedFields) => {
    try {
      setFlashSaving(true);
      if (flashEditingOpportunity.type === "contrato") {
        await updateContrato({ id: flashEditingOpportunity.id, contrato: updatedFields });
        bumpVersionDatos();
        toast.success("Contrato actualizado correctamente");
        setContratos((prev) =>
          prev.map((op) => (op.id === flashEditingOpportunity.id ? { ...op, ...updatedFields } : op))
        );
      } else {
        await updateOportunidad({ id: flashEditingOpportunity.id, oportunidad: updatedFields });
        bumpVersionDatos();
        toast.success("Oportunidad actualizada correctamente");
        setOportunidades((prev) =>
          prev.map((op) => (op.id === flashEditingOpportunity.id ? { ...op, ...updatedFields } : op))
        );
      }
      setIsFlashEditing(false);
      setFlashEditingOpportunity(null);
    } catch (error) {
      console.error("Error updating oportunidad:", error);
      toast.error("Error al actualizar la oportunidad");
    } finally {
      setFlashSaving(false);
    }
  };

  // Persiste la asignación de edificios de una cuota: clona meses, patchea meses[idx] con el
  // payload de ModalAsignarEdificios y escribe con updateContrato directo (mismo shape que
  // escribía handleSaveEdificiosCuota en el flash). Refresca el state local sin recargar.
  const handleSaveEdificiosCuota = async (
    idsString,
    edificiosTotales,
    pantallasTotales,
    personasAsignadas,
    perTypeData
  ) => {
    if (!edificiosModalData) return;
    const { contratoId, idx } = edificiosModalData;
    try {
      const contrato = contratos.find((c) => c.id === contratoId);
      if (!contrato) throw new Error("Contrato no encontrado");
      const meses = [...(contrato.meses || [])];
      meses[idx] = {
        ...meses[idx],
        edificios_ids: idsString || null,
        edificios_totales: edificiosTotales,
        pantallas_totales: pantallasTotales,
        personas_asignadas: personasAsignadas,
        pantallas_ascensores: perTypeData?.pantallas_ascensores || "",
        pantallas_espera: perTypeData?.pantallas_espera || "",
        pantallas_circulacion: perTypeData?.pantallas_circulacion || "",
        pantallas_cowork: perTypeData?.pantallas_cowork || "",
      };
      await updateContrato({ id: contratoId, contrato: { meses } });
      setContratos((prev) => prev.map((c) => (c.id === contratoId ? { ...c, meses } : c)));
      toast.success("Edificios asignados correctamente");
      setEdificiosModalData(null);
    } catch (error) {
      console.error("Error asignando edificios:", error);
      toast.error("Error al asignar edificios");
    }
  };

  // Props derivadas del modal de edificios abierto (cuota + contrato en contexto).
  const edificiosCuotaCtx = (() => {
    if (!edificiosModalData) return null;
    const contrato = contratos.find((c) => c.id === edificiosModalData.contratoId);
    const cuota = contrato?.meses?.[edificiosModalData.idx];
    if (!cuota) return null;
    const [mm, yyyy] = (cuota.mes || "").split("-");
    return {
      personasComprometidas: contrato.personas_comprometidas || 0,
      edificiosIds: cuota.edificios_ids || null,
      mesLabel: `${mesEnLetras(mm)}/${yyyy || ""}`,
    };
  })();

  const getRadius = (key, data) => {
    const keys = Object.keys(data).filter((k) => data[k] > 0);
    const firstKey = keys[0];
    const lastKey = keys.at(-1);
    if (firstKey === key && lastKey === key) return [10, 10, 10, 10];
    if (firstKey === key) return [10, 0, 0, 10];
    if (lastKey === key) return [0, 10, 10, 0];
    return [0, 0, 0, 0];
  };

  const customStatusOrder = { "100%": 0, "90%": 1, "75%": 2, "50%": 3, "0%": 4 };

  // Atribución AdLab de UNA fila (Perú). Fuente única para ADLAB y su complemento Visiona.
  //  - porEjecutivoBroker: la venta la creó un usuario rol broker de esa agencia (PRESERVADO).
  //  - predicadoCuentaPeríodo: BTL por CUENTA (sin período — sales_btl no tiene fecha_cierre/
  //    excluir_broker); sales/contrato período-aware vía esVentaEnPeriodoBroker (respeta
  //    excluir_broker, fecha_cierre y 100% cerrada pre-junio).
  const esFilaAdLab = (oportunidad, interId, cliente) => {
    const u = users.find((uu) => String(uu.id) === String(oportunidad.ejecutivoId));
    const porEjecutivoBroker = !!u && u.rol === ROLES.BROKER && String(u.agencia) === String(interId);
    const predicadoCuentaPeriodo =
      oportunidad.type === "btl"
        ? String(cliente?.broker_id) === String(interId)
        : esVentaEnPeriodoBroker(oportunidad, cliente);
    return porEjecutivoBroker || predicadoCuentaPeriodo;
  };

  // Tras "Asignar a oportunidad": parche puntual con el estado final que devuelve la RPC (10.234),
  // nunca recarga completa. Toca la fila movida y, si el destino subió de estatus, sus hermanas.
  // Después avisa al resto del hub (Pizarra/CRM leen estatus).
  const onOportunidadAsignada = (resultado) => {
    const {
      oportunidad: destino,
      origen_id: origenId,
      origen_eliminada: origenEliminada,
      movida,
      estatus_subio: estatusSubio,
      cotizaciones = [],
    } = resultado;
    setOportunidadesInfo((prev) => {
      const next = new Map(prev);
      next.set(Number(destino.id), {
        id: destino.id,
        nombre: destino.nombre,
        estatus: destino.estatus,
        eliminada: false,
      });
      if (origenEliminada) next.delete(Number(origenId));
      return next;
    });
    setOportunidades((prev) => parchearFilasAsignadas(prev, cotizaciones, "sale"));
    setContratos((prev) => parchearFilasAsignadas(prev, cotizaciones, "contrato"));
    bumpVersionDatos();
    setAsignarOportunidadFila(null);
    if (movida) {
      toast.success(
        `Cotización asignada a "${destino.nombre}"` +
          (estatusSubio ? ` (la oportunidad sube a ${destino.estatus})` : "") +
          (origenEliminada ? ". La oportunidad anterior quedó sin cotizaciones y se dio de baja." : ".")
      );
    } else {
      toast.success(`Oportunidad renombrada a "${destino.nombre}".`);
    }
  };

  // Columna, acción y filtro de oportunidad se encienden solos cuando las filas traen la columna
  // `oportunidad_id` (migración de la fase 1 aplicada). Sin ella, Detalle queda idéntico (10.208).
  const oportunidadesHabilitadas = useMemo(
    () => [...oportunidades, ...contratos].some((o) => Object.prototype.hasOwnProperty.call(o, "oportunidad_id")),
    [oportunidades, contratos]
  );
  const nombreOportunidad = (fila) =>
    fila.oportunidad_id != null ? (oportunidadesInfo.get(Number(fila.oportunidad_id))?.nombre ?? null) : null;
  // "Contiene", con la normalización compartida trim + mayúsculas + sin tildes (10.1).
  const filtroOportunidadNorm = oportunidadesHabilitadas ? normalizarEmpresa(filtroOportunidad) : "";

  // Columnas ocultas por ancho de pantalla (2026-09-30) — solo con `oportunidadesHabilitadas`
  // (ver comentario de `ANCHOS_POR_TRAMO` arriba); con la feature apagada la tabla no se toca.
  const ocultarIdCodeRaw = useMenorQuePx(UMBRALES_COLUMNAS_DETALLE_PX.OCULTAR_ID_CODE);
  const ocultarIntermediarioRaw = useMenorQuePx(UMBRALES_COLUMNAS_DETALLE_PX.OCULTAR_INTERMEDIARIO);
  const ocultarIdCode = oportunidadesHabilitadas && ocultarIdCodeRaw;
  const ocultarIntermediario = oportunidadesHabilitadas && ocultarIntermediarioRaw;
  const tramoAnchoTabla = ocultarIntermediario ? "sinIdCodeIntermediario" : ocultarIdCode ? "sinIdCode" : "completo";
  const anchosTabla = ANCHOS_POR_TRAMO[tramoAnchoTabla][modoComparar ? "conComparar" : "sinComparar"];

  const oportunidadesFiltradas = [...oportunidades, ...oportunidadesBTL, ...contratos].filter((oportunidad) => {
    const oportunidadMatch =
      !filtroOportunidadNorm || normalizarEmpresa(nombreOportunidad(oportunidad) || "").includes(filtroOportunidadNorm);
    if (!oportunidadMatch) return false;
    const cliente = clientes.find((c) => c.id === oportunidad.client_id && c.pais === pais);
    const clienteMatch = !filtroCliente || (cliente && cliente.empresa === filtroCliente);
    const productoMatch = !filtroProducto || (cliente && cliente.producto === filtroProducto);

    // Extraer mes y año de cierre de la oportunidad
    const mesCierreOportunidad = oportunidad.mes_cierre ? oportunidad.mes_cierre.split("-")[0] : null;
    const anoCierreOportunidad = oportunidad.mes_cierre ? oportunidad.mes_cierre.split("-")[1] : null;

    // Filtro combinado de cierre: mes Y año deben coincidir cuando ambos están seleccionados
    const cierreMatch = (() => {
      const hayFiltroMes = filtroMesCierre.length > 0;
      const hayFiltroAno = filtroAnoCierre.length > 0;
      if (!hayFiltroMes && !hayFiltroAno) return true;
      if (!mesCierreOportunidad || !anoCierreOportunidad) return false;
      const mesCoincide = !hayFiltroMes || filtroMesCierre.includes(mesCierreOportunidad);
      const anoCoincide = !hayFiltroAno || filtroAnoCierre.includes(anoCierreOportunidad);
      return mesCoincide && anoCoincide;
    })();

    // "Año factura"/"Mes factura" son PROYECCIÓN (decisión de Ignacio, 2026-09-21): filtran por
    // el mes en que se PREVÉ facturar, no exigen que ya esté facturada. Por eso priorizan
    // facturafecha (ya facturada, el dato real) y caen a mes_factura (la proyección, aún sin
    // facturar) — cualquiera de las dos sirve, no hace falta que esté facturada.
    const mesFacturaOportunidad = oportunidad.facturafecha
      ? String(new Date(oportunidad.facturafecha).getMonth() + 1).padStart(2, "0")
      : oportunidad.mes_factura
        ? oportunidad.mes_factura.split("-")[0]
        : null;
    const anoFacturaOportunidad = oportunidad.facturafecha
      ? new Date(oportunidad.facturafecha).getFullYear().toString()
      : oportunidad.mes_factura
        ? oportunidad.mes_factura.split("-")[1]
        : null;

    // Filtro combinado de factura: mes Y año deben coincidir cuando ambos están seleccionados.
    // Sin filtro activo (default), no exige nada — deja pasar todo (ver defaults arriba: el
    // filtro de año factura ya NO viene precargado, así que esto es "sin filtro" salvo que el
    // usuario elija un año/mes de proyección a propósito. Una oportunidad SIN mes_factura NI
    // facturafecha, con el filtro activo, queda AFUERA (no hay proyección → no hay año que
    // comparar; es consistente con la definición de "proyección", no un bug).
    const facturaMatch = (() => {
      const hayFiltroMes = filtroMes.length > 0;
      const hayFiltroAno = filtroAnoFactura.length > 0;
      if (!hayFiltroMes && !hayFiltroAno) return true;
      if (!mesFacturaOportunidad || !anoFacturaOportunidad) return false;
      const mesCoincide = !hayFiltroMes || filtroMes.includes(mesFacturaOportunidad);
      const anoCoincide = !hayFiltroAno || filtroAnoFactura.includes(anoFacturaOportunidad);
      return mesCoincide && anoCoincide;
    })();

    const estatusMatch = filtroEstatus.length === 0 || filtroEstatus.includes(oportunidad.estatus);
    const categoriaMatch =
      !filtroCategoria || String(clienteCategoriaIdMap[oportunidad.client_id]?.categoriaId) === String(filtroCategoria);
    const subcategoriaMatch =
      !filtroSubcategoria ||
      String(clienteCategoriaIdMap[oportunidad.client_id]?.subcategoriaId) === String(filtroSubcategoria);
    let ejecutivoMatch;
    if (!filtroEjecutivo) {
      ejecutivoMatch = true;
    } else if (filtroEjecutivo.startsWith("broker:")) {
      // ADLAB unificado (período-aware): ejecutivo broker de esa agencia, O fila atribuida a
      // AdLab por cuenta/período (esVentaEnPeriodoBroker en sales/contrato; cuenta en BTL).
      // `cliente` ya resuelto arriba (757). Reemplaza el enPeriodoBroker inline laxo.
      ejecutivoMatch = esFilaAdLab(oportunidad, filtroEjecutivo.slice("broker:".length), cliente);
    } else if (filtroEjecutivo === "grupo:visiona") {
      // Visiona = complemento EXACTO de AdLab sobre la MISMA fila (misma fuente esFilaAdLab).
      ejecutivoMatch = !esFilaAdLab(oportunidad, AGENCIA_ADLAB_PERU, cliente);
    } else {
      ejecutivoMatch = String(oportunidad.ejecutivoId) === String(filtroEjecutivo);
    }
    const intermediaryId = clienteIntermediarioMap[oportunidad.client_id] || null;
    const intermediarioMatch = matchAgenciaFilter(filtroIntermediario, intermediaryId, pais);
    return (
      clienteMatch &&
      productoMatch &&
      cierreMatch &&
      facturaMatch &&
      estatusMatch &&
      intermediarioMatch &&
      categoriaMatch &&
      subcategoriaMatch &&
      ejecutivoMatch &&
      !oportunidad.eliminada
    );
  });

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const toggleContrato = (id) => {
    setExpandedContratos((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const parseMesAno = (mesAnoStr) => {
    if (!mesAnoStr || typeof mesAnoStr !== "string") return 0;
    const parts = mesAnoStr.split("-");
    if (parts.length !== 2) return 0;
    const mes = parseInt(parts[0], 10);
    const ano = parseInt(parts[1], 10);
    return ano * 12 + mes;
  };

  const sortedOportunidades = [...oportunidadesFiltradas].sort((a, b) => {
    // Si hay un sortConfig activo, usarlo
    if (sortConfig.key) {
      const direction = sortConfig.direction === "asc" ? 1 : -1;

      switch (sortConfig.key) {
        case "id":
          return direction * (parseInt(a.id) - parseInt(b.id));
        case "cliente":
          return direction * (a.cliente || "").localeCompare(b.cliente || "");
        case "campana":
          // CAMPAÑA fusiona OPORTUNIDAD (Fase 1b): ordena por el texto MOSTRADO, no por el campo
          // crudo — nombre de oportunidad si la fila tiene una, si no la campaña de la cotización.
          return (
            direction * (nombreOportunidad(a) || a.campana || "").localeCompare(nombreOportunidad(b) || b.campana || "")
          );
        case "cierre": {
          const cierreA = parseMesAno(a.mes_cierre);
          const cierreB = parseMesAno(b.mes_cierre);
          return direction * (cierreA - cierreB);
        }
        case "factura": {
          const facturaA = a.facturafecha
            ? new Date(a.facturafecha).getTime()
            : parseMesAno(a.mes_factura) * 2629800000;
          const facturaB = b.facturafecha
            ? new Date(b.facturafecha).getTime()
            : parseMesAno(b.mes_factura) * 2629800000;
          return direction * (facturaA - facturaB);
        }
        case "ticket": {
          // Sort consistente: convertir todo a USD via rate histórico
          const importeA = getImporteEnUSD(a, getDolarValue, fallbackRate);
          const importeB = getImporteEnUSD(b, getDolarValue, fallbackRate);
          return direction * (importeA - importeB);
        }
        case "status": {
          const statusA = customStatusOrder[a.estatus || "0%"];
          const statusB = customStatusOrder[b.estatus || "0%"];
          return direction * (statusA - statusB);
        }
        case "ejecutivo":
          return direction * (a.ejecutivo || "").localeCompare(b.ejecutivo || "");
        case "tipo":
          return direction * tipoVenta(a).localeCompare(tipoVenta(b));
        default:
          break;
      }
    }

    // Ordenamiento por defecto:
    //   1. 100% al tope, ordenados por mes_factura ASC (YTD)
    //   2. 90% → 75% → 50% en el medio, por importe DESC dentro de cada status
    //   3. 0% al fondo, ordenados por mes_cierre ASC
    const statusA = customStatusOrder[a.estatus || "0%"];
    const statusB = customStatusOrder[b.estatus || "0%"];
    if (statusA !== statusB) {
      return statusA - statusB;
    }
    const importeA = getImporteEnUSD(a, getDolarValue, fallbackRate);
    const importeB = getImporteEnUSD(b, getDolarValue, fallbackRate);
    const estatus = a.estatus || "0%";
    if (estatus === "100%") {
      const facturaA = parseMesAno(a.mes_factura);
      const facturaB = parseMesAno(b.mes_factura);
      if (facturaA !== facturaB) return facturaA - facturaB;
      return importeB - importeA;
    }
    if (estatus === "0%") {
      const cierreA = parseMesAno(a.mes_cierre);
      const cierreB = parseMesAno(b.mes_cierre);
      if (cierreA !== cierreB) return cierreA - cierreB;
      return importeB - importeA;
    }
    return importeB - importeA;
  });

  const renderEjecutivosOptions = () => {
    const countryMapping = { Uruguay: "URU", Perú: "PER", Chile: "CHI" };
    const countryCode = countryMapping[pais] || "";
    const ejecutivosUnicos = users
      .filter((user) => {
        const userPais = user.pais || "";
        const userRol = user.rol ? user.rol.toLowerCase().trim() : "";
        const validRoles = ["comercial", "gerencia"].map((r) => r.toLowerCase());
        return (userPais === countryCode || !countryCode) && validRoles.includes(userRol) && user.habilitado === true;
      })
      .sort((a, b) => `${a.nombre} ${a.apellido}`.localeCompare(`${b.nombre} ${b.apellido}`));

    // Brokers: se agrupan bajo su intermediario (ej. "AdLab") como una sola opción
    // adicional. value = "broker:<intermediarioId>" para que el predicado matchee a
    // todos los brokers de esa agencia (1623/1624 → AdLab).
    const brokerInterIds = [
      ...new Set(
        users
          .filter((u) => {
            const userPais = u.pais || "";
            return (userPais === countryCode || !countryCode) && u.rol === ROLES.BROKER && u.agencia;
          })
          .map((u) => String(u.agencia))
      ),
    ];

    return [
      ...ejecutivosUnicos.map((user) => (
        <option key={`u-${user.id}`} value={user.id}>
          {`${user.nombre} ${user.apellido}`}
        </option>
      )),
      ...brokerInterIds.map((interId) => (
        <option key={`b-${interId}`} value={`broker:${interId}`}>
          {intermediaryMap[interId] || `Broker ${interId}`}
        </option>
      )),
      // Visiona = complemento exacto de AdLab. Solo Perú (hay ≥1 agencia broker); en Chile no se emite.
      ...(brokerInterIds.length > 0
        ? [
            <option key="grupo-visiona" value="grupo:visiona">
              Visiona
            </option>,
          ]
        : []),
    ];
  };

  const handleShowDetails = (oportunidad) => {
    setSelectedOpportunity(oportunidad);
    setShowDetailsModal(true);
  };

  const toggleBuildingsList = () => {
    setIsBuildingsExpanded(!isBuildingsExpanded);
  };

  // --- Modo Comparar ---
  const clientesConMultiples = useMemo(() => {
    const conteo = {};
    sortedOportunidades.forEach((op) => {
      if (!op.client_id) return;
      const estatus = op.estatus;
      if (estatus === "50%" || estatus === "75%" || estatus === "90%") {
        conteo[op.client_id] = (conteo[op.client_id] || 0) + 1;
      }
    });
    const ids = new Set();
    Object.entries(conteo).forEach(([clientId, count]) => {
      if (count >= 2) ids.add(Number(clientId));
    });
    return ids;
  }, [sortedOportunidades]);

  const oportunidadesComparar = useMemo(() => {
    if (!modoComparar) return sortedOportunidades;
    const ESTATUS_COMPARABLES = new Set(["50%", "75%", "90%"]);
    const filtered = sortedOportunidades.filter(
      (op) => op.client_id && clientesConMultiples.has(Number(op.client_id)) && ESTATUS_COMPARABLES.has(op.estatus)
    );
    return [...filtered].sort((a, b) => {
      const clientCmp = String(a.cliente || "").localeCompare(String(b.cliente || ""));
      if (clientCmp !== 0) return clientCmp;
      return (Number(b.importe) || 0) - (Number(a.importe) || 0);
    });
  }, [modoComparar, sortedOportunidades, clientesConMultiples]);

  // Agrupación visual por oportunidad (rowspan, solo presentación — 2026-09-29). Las filas ya
  // vienen ordenadas (por sortConfig o por el criterio de modoComparar); acá solo se reordena
  // para que las de la MISMA oportunidad queden contiguas, conservando su orden relativo interno
  // (estable: el comparador ya las dejó en ese orden) y la posición del grupo la da la primera
  // aparición de esa oportunidad en la lista ya ordenada — así "ordenar por OPORTUNIDAD" ordena
  // por nombre de grupo sin lógica aparte. Filas sin `oportunidad_id` (arqueología, BTL) quedan
  // sueltas, cada una su propio grupo de 1. `rowSpanGrupo` de la primera fila del grupo incluye
  // las sub-filas de cuota visibles (acordeón de contratos) de TODAS las filas del grupo, para
  // que la celda fusionada no desalinee la tabla cuando hay un contrato expandido.
  const filasConGrupo = useMemo(() => {
    const listaBase = modoComparar ? oportunidadesComparar : sortedOportunidades;
    if (!oportunidadesHabilitadas) {
      return listaBase.map((fila) => ({ fila, esPrimeraDelGrupo: true, rowSpanGrupo: 1 }));
    }
    const gruposMap = new Map();
    const ordenPrimeraAparicion = [];
    listaBase.forEach((fila) => {
      const key =
        fila.oportunidad_id != null ? `opp-${fila.oportunidad_id}` : `sola-${fila.type || "regular"}-${fila.id}`;
      if (!gruposMap.has(key)) {
        gruposMap.set(key, []);
        ordenPrimeraAparicion.push(key);
      }
      gruposMap.get(key).push(fila);
    });
    const resultado = [];
    ordenPrimeraAparicion.forEach((key, grupoIndex) => {
      const grupo = gruposMap.get(key);
      const subfilasVisibles = grupo.reduce(
        (acc, f) =>
          acc + (f.type === "contrato" && expandedContratos.has(f.id) && Array.isArray(f.meses) ? f.meses.length : 0),
        0
      );
      const rowSpanGrupo = grupo.length + subfilasVisibles;
      grupo.forEach((fila, idx) => {
        resultado.push({ fila, esPrimeraDelGrupo: idx === 0, rowSpanGrupo: idx === 0 ? rowSpanGrupo : 0, grupoIndex });
      });
    });
    return resultado;
  }, [modoComparar, oportunidadesComparar, sortedOportunidades, oportunidadesHabilitadas, expandedContratos]);

  const handleToggleComparar = () => {
    setModoComparar((prev) => !prev);
    setSeleccionComparar([]);
  };

  const handleSelectComparar = (oportunidad) => {
    setSeleccionComparar((prev) => {
      const yaSeleccionada = prev.find(
        (s) => s.id === oportunidad.id && (s.type || "regular") === (oportunidad.type || "regular")
      );
      if (yaSeleccionada) {
        return prev.filter(
          (s) => !(s.id === oportunidad.id && (s.type || "regular") === (oportunidad.type || "regular"))
        );
      }
      if (prev.length > 0 && Number(prev[0].client_id) !== Number(oportunidad.client_id)) {
        return [oportunidad];
      }
      if (prev.length >= 4) return prev;
      return [...prev, oportunidad];
    });
  };

  const isSelectedComparar = (oportunidad) =>
    seleccionComparar.some((s) => s.id === oportunidad.id && (s.type || "regular") === (oportunidad.type || "regular"));

  const selectedClientId = seleccionComparar.length > 0 ? Number(seleccionComparar[0].client_id) : null;

  const salesData = useMemo(() => {
    // Usamos exactamente las oportunidades que ya están filtradas y ordenadas para la tabla
    const oportunidadesVisibles = sortedOportunidades;

    // Conversión por op según viewInUSD: rate histórico de calculateVentas.js
    const getValor = (op) => (viewInUSD ? getImporteEnUSD(op, getDolarValue, fallbackRate) : getImporteLocal(op));

    // Filtrar por estatus
    const ops100 = oportunidadesVisibles.filter((op) => op.estatus === "100%");
    const ops90 = oportunidadesVisibles.filter((op) => op.estatus === "90%");
    const ops75 = oportunidadesVisibles.filter((op) => op.estatus === "75%");
    const ops50 = oportunidadesVisibles.filter((op) => op.estatus === "50%");
    const ops0 = oportunidadesVisibles.filter((op) => op.estatus === "0%");

    // Proyección de pipeline por estatus (10.272): 100% suma todo (cerrado, cada fila es venta
    // real); 90/75/50/0% cuentan una sola oportunidad por cliente y estatus, la de menor monto
    // (alternativas de una misma campaña). Fuente única con el CRM y VentasMiListaClientes.
    const suma100 = sumarImportesPorEstatus(ops100, getValor, "100%");
    const suma90 = sumarImportesPorEstatus(ops90, getValor, "90%");
    const suma75 = sumarImportesPorEstatus(ops75, getValor, "75%");
    const suma50 = sumarImportesPorEstatus(ops50, getValor, "50%");
    const suma0 = sumarImportesPorEstatus(ops0, getValor, "0%");

    const total = suma100 + suma90 + suma75 + suma50 + suma0;

    return {
      sumaImportes100: Math.round(suma100),
      sumaImportes90: Math.round(suma90),
      sumaImportes75: Math.round(suma75),
      sumaImportes50: Math.round(suma50),
      sumaImportes0: Math.round(suma0),
      chartData: [
        {
          name: "Total",
          "100%": total ? (suma100 / total) * 100 : 0,
          "90%": total ? (suma90 / total) * 100 : 0,
          "75%": total ? (suma75 / total) * 100 : 0,
          "50%": total ? (suma50 / total) * 100 : 0,
          "0%": total ? (suma0 / total) * 100 : 0,
          empty: total === 0 ? 100 : 0,
        },
      ],
    };
  }, [sortedOportunidades, viewInUSD, getDolarValue, fallbackRate, getImporteLocal]);

  const filtersContent = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        gap: "16px",
      }}
    >
      <div className={styles.filtersContainer} style={{ flex: 1 }}>
        {/* Fila 1: Empresa, Producto, Categoría, Agencia, Ejecutivo */}
        <div className={styles.filterRow}>
          <div>
            <select
              className={`form-control ${filtroCliente ? styles.activeFilter : ""}`}
              value={filtroCliente}
              onChange={(e) => onChangeEmpresa(e.target.value)}
              disabled={empresaDisabled}
              style={empresaDisabled ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
            >
              <option value="">{empresaDisabled ? empresasDeProducto[0] : "Empresa"}</option>
              {renderClientesOptions()}
            </select>
          </div>
          <div>
            <select
              className={`form-control ${filtroProducto ? styles.activeFilter : ""}`}
              value={filtroProducto}
              onChange={(e) => onChangeProducto(e.target.value)}
              disabled={productoDisabled}
              style={productoDisabled ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
            >
              <option value="">{productoDisabled ? productosDeEmpresa[0] : "Producto"}</option>
              {renderProductosOptions()}
            </select>
          </div>
          <CategoriaSubcategoriaFilter
            categorias={categorias}
            subcategorias={subcategorias}
            filtroCategoria={filtroCategoria}
            filtroSubcategoria={filtroSubcategoria}
            onCategoriaChange={setFiltroCategoria}
            onSubcategoriaChange={setFiltroSubcategoria}
            className="form-control"
            activeClass={styles.activeFilter}
          />
          <div>
            <AgenciasDropdown
              value={filtroIntermediario}
              onChange={(e) => setFiltroIntermediario(e.target.value)}
              intermediarios={intermediarios}
              placeholder="Agencia"
              className={`form-control ${filtroIntermediario ? styles.activeFilter : ""}`}
            />
          </div>
          <div>
            <select
              className={`form-control ${filtroEjecutivo ? styles.activeFilter : ""}`}
              value={filtroEjecutivo}
              onChange={(e) => setFiltroEjecutivo(e.target.value)}
            >
              <option value="">Ejecutivo</option>
              {renderEjecutivosOptions()}
            </select>
          </div>
        </div>
        {/* Fila 2: Mes Cierre, Año Cierre, Mes Factura, Año Factura, Status */}
        <div className={styles.filterRow}>
          <div>
            <Dropdown>
              <Dropdown.Toggle
                variant="white border"
                id="dropdown-mes-cierre"
                className={`form-control text-start ${filtroMesCierre.length > 0 ? styles.activeFilter : ""}`}
              >
                {filtroMesCierre.length === 0
                  ? "Mes cierre"
                  : filtroMesCierre.length >= 4
                    ? "Varios"
                    : filtroMesCierre.map((mes) => mesEnLetras(mes)).join(", ")}
              </Dropdown.Toggle>
              <Dropdown.Menu>{renderMesOptions(filtroMesCierre, handleMesCierreChange)}</Dropdown.Menu>
            </Dropdown>
          </div>
          <div>
            <Dropdown>
              <Dropdown.Toggle
                variant="white border"
                id="dropdown-ano-cierre"
                className={`form-control text-start ${filtroAnoCierre.length > 0 ? styles.activeFilter : ""}`}
              >
                Año cierre
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {aniosCierreDisponibles.map((ano, index) => (
                  <div key={index} className="form-check left-aligned-checkbox">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`anoCierre${index}`}
                      value={ano}
                      checked={filtroAnoCierre.includes(ano)}
                      onChange={handleAnoCierreChange}
                    />
                    <label className="form-check-label" htmlFor={`anoCierre${index}`}>
                      {ano}
                    </label>
                  </div>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
          <div>
            <Dropdown>
              <Dropdown.Toggle
                variant="white border"
                id="dropdown-mes-factura"
                className={`form-control text-start ${filtroMes.length > 0 ? styles.activeFilter : ""}`}
              >
                {filtroMes.length === 0
                  ? "Mes factura"
                  : filtroMes.length >= 4
                    ? "Varios"
                    : filtroMes.map((mes) => mesEnLetras(mes)).join(", ")}
              </Dropdown.Toggle>
              <Dropdown.Menu>{renderMesOptions(filtroMes, handleMesChange)}</Dropdown.Menu>
            </Dropdown>
          </div>
          <div>
            <Dropdown>
              <Dropdown.Toggle
                variant="white border"
                id="dropdown-ano-factura"
                className={`form-control text-start ${filtroAnoFactura.length > 0 ? styles.activeFilter : ""}`}
              >
                Año factura
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {aniosFacturaDisponibles.map((ano, index) => (
                  <div key={index} className="form-check left-aligned-checkbox">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`anoFactura${index}`}
                      value={ano}
                      checked={filtroAnoFactura.includes(ano)}
                      onChange={handleAnoFacturaChange}
                    />
                    <label className="form-check-label" htmlFor={`anoFactura${index}`}>
                      {ano}
                    </label>
                  </div>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
          <div>
            <Dropdown>
              <Dropdown.Toggle
                variant="white border"
                id="dropdown-status"
                className={`form-control text-start ${filtroEstatus.length > 0 ? styles.activeFilter : ""}`}
              >
                {filtroEstatus.length === 0
                  ? "Status"
                  : filtroEstatus.length >= 4
                    ? "Varios"
                    : filtroEstatus.join(", ")}
              </Dropdown.Toggle>
              <Dropdown.Menu>{renderEstatusOptions()}</Dropdown.Menu>
            </Dropdown>
          </div>
          {oportunidadesHabilitadas && (
            <div>
              <input
                type="text"
                className={`form-control ${filtroOportunidad.trim() ? styles.activeFilter : ""}`}
                placeholder="Oportunidad"
                aria-label="Filtrar por oportunidad"
                value={filtroOportunidad}
                onChange={(e) => setFiltroOportunidad(e.target.value)}
              />
            </div>
          )}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: "8px",
          marginRight: "16px",
        }}
      >
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            whiteSpace: "nowrap",
            cursor: "pointer",
            fontSize: "0.85rem",
            color: "#000",
          }}
        >
          Ver en dólares
          <input
            type="checkbox"
            checked={viewInUSD}
            onChange={(e) => setViewInUSD(e.target.checked)}
            disabled={!ratesLoaded}
          />
        </label>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            whiteSpace: "nowrap",
            cursor: "pointer",
            fontSize: "0.85rem",
            color: "#000",
          }}
        >
          Comparar
          <input type="checkbox" checked={modoComparar} onChange={handleToggleComparar} />
        </label>
      </div>
    </div>
  );

  // Publica los filtros en el header del shell (hook incondicional — VentasDetalle no tiene
  // ningún early return antes de este punto, así que siempre corre). Fuera del hub (sin tabKey)
  // es un no-op y se usa el renderHeader/fallback de siempre, más abajo.
  useHubHeaderFilters(tabKey, filtersContent);

  // Columnas reales del thead (Fase 1b, 2026-10-04; +OM en Fase OM.2, 2026-10-05): ID, CLIENTE,
  // CAMPAÑA (fusiona OPORTUNIDAD, siempre 1 sola columna ahora — ya no suma condicional), CIERRE,
  // FACTURA, TICKET, STATUS, OM, CODE, TIPO, INVENTARIO, AGENCIA (ex-INTERMEDIARIO), EJECUTIVO,
  // ACCIONES — 14 columnas de 1, SIEMPRE (ACCIONES es una sola celda con `<KebabMenu>`, "Asignar a
  // oportunidad" vive adentro, no suma celda propia; OM nunca se oculta por ancho, siempre 1 celda
  // angosta). +1 columna de checkbox cuando modoComparar. Derivado acá para que la fila vacía
  // coincida siempre con el thead real (antes hardcodeado a mano, deformaba la cabecera). Columnas
  // ocultas por ancho de pantalla (2026-09-30, ver ANCHOS_POR_TRAMO): ID+CODE restan 2, AGENCIA
  // resta 1 más — de verdad no se renderizan, así que también restan del colSpan.
  const COLUMNAS_TABLA_BASE = 14;
  const columnasOcultasPorAncho = (ocultarIdCode ? 2 : 0) + (ocultarIntermediario ? 1 : 0);
  const totalColumnasTabla = COLUMNAS_TABLA_BASE + (modoComparar ? 1 : 0) - columnasOcultasPorAncho;

  return (
    // Regresión 2026-09-21 (marco de página): con tabKey (dentro del hub) el marco de 15px ya lo
    // aporta el shell (Ventas.module.scss .wrapper) — acá se anula el propio para no duplicarlo.
    // Standalone (sin tabKey, fuera del hub) conserva su padding:15px de siempre.
    <div className="container-ventas-detalle" style={tabKey ? { padding: 0 } : undefined}>
      {tabKey ? null : renderHeader ? renderHeader(filtersContent) : filtersContent}
      {loadingData && (
        // Estado de carga honesto (10.177): cargando ≠ sin datos. Mientras la carga inicial está
        // en curso, se muestra un loader acá y las tarjetas/barra/tabla de abajo quedan ocultas
        // (d-none, ver su className) — nunca se ve "No hay ventas ni oportunidades..." ni la
        // barra "SIN DATOS", que son el resultado de un filtro sin coincidencias, no de datos que
        // todavía no llegaron.
        <div className="row justify-content-center" style={{ minHeight: 300 }}>
          <div className="col-md-12 d-flex align-items-center justify-content-center">
            <Loader />
          </div>
        </div>
      )}
      <div className={`row justify-content-center mb-2${loadingData ? " d-none" : ""}`}>
        <div className="col-md-12">
          <div className="d-flex justify-content-between">
            {/* 100% */}
            <div
              className="text-center p-2 bg-success border border-dark m-3 text-white"
              style={{ borderRadius: "4px", width: "225px" }}
            >
              <div>
                <strong>100%</strong>
              </div>
              <div>{salesData.sumaImportes100 > 0 ? formatMoneda(salesData.sumaImportes100, viewInUSD) : "-"}</div>
            </div>

            {/* 90% */}
            <div
              className="text-center p-2 bg-danger border border-dark m-3 text-white"
              style={{ borderRadius: "4px", width: "225px" }}
            >
              <div>
                <strong>90%</strong>
              </div>
              <div>{salesData.sumaImportes90 > 0 ? formatMoneda(salesData.sumaImportes90, viewInUSD) : "-"}</div>
            </div>

            {/* 75% */}
            <div
              className="text-center border border-dark p-2 bg-primary m-3 text-white"
              style={{ borderRadius: "4px", width: "225px" }}
            >
              <div>
                <strong>75%</strong>
              </div>
              <div>{salesData.sumaImportes75 > 0 ? formatMoneda(salesData.sumaImportes75, viewInUSD) : "-"}</div>
            </div>

            {/* 50% */}
            <div
              className="text-center p-2 bg-warning m-3 border border-dark text-dark"
              style={{ borderRadius: "4px", width: "225px" }}
            >
              <div>
                <strong>50%</strong>
              </div>
              <div>{salesData.sumaImportes50 > 0 ? formatMoneda(salesData.sumaImportes50, viewInUSD) : "-"}</div>
            </div>

            {/* 0% */}
            <div
              className="text-center p-2 bg-black m-3 border border-dark text-white"
              style={{ borderRadius: "4px", width: "225px" }}
            >
              <div>
                <strong>0%</strong>
              </div>
              <div>{salesData.sumaImportes0 > 0 ? formatMoneda(salesData.sumaImportes0, viewInUSD) : "-"}</div>
            </div>
          </div>
        </div>
      </div>
      <div className={`row justify-content-center chart-barra-row${loadingData ? " d-none" : ""}`}>
        <div className="col-md-12">
          {/* Revertido a 80 (Fase 2, 2026-10-05): Ignacio pidió reducir el ESPACIO entre la barra
              y la tabla, no la barra — el 80px→40px de la Fase 1b aplastaba el BarChart entero
              (barra + labels de %) porque recharts dimensiona el grosor de la barra en proporción
              a la altura del contenedor. Alto exacto al de antes de esta sesión (ver
              da2f5272^:src/components/VentasDetalle/index.js). El espacio sobrante entre la barra
              y la tabla se recorta aparte con CSS (`.chart-barra-row`, stylesVentasDetalle.scss),
              sin tocar esta prop ni ninguna otra de recharts. */}
          <ResponsiveContainer width="100%" height={80} className="p-0 m-0">
            <BarChart layout="vertical" data={salesData.chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
              <XAxis type="number" domain={[0, 100]} tick={false} axisLine={false} />
              <YAxis type="category" dataKey="name" hide />

              {/* Acá está el fix: todo chartData → salesData.chartData */}
              {salesData.chartData[0].empty > 0 ? (
                <Bar dataKey="empty" fill="#555555" isAnimationActive={false} radius={[10, 10, 10, 10]}>
                  <LabelList dataKey="empty" position="center" formatter={() => "SIN DATOS"} fill="#FFF" />
                </Bar>
              ) : (
                <>
                  {salesData.chartData[0]["100%"] > 0 && (
                    <Bar dataKey="100%" stackId="a" fill="#198754" radius={getRadius("100%", salesData.chartData[0])}>
                      <LabelList
                        dataKey="100%"
                        position="center"
                        formatter={(value) => `${Math.round(value)}%`}
                        fill="#fff"
                      />
                    </Bar>
                  )}
                  {salesData.chartData[0]["90%"] > 0 && (
                    <Bar dataKey="90%" stackId="a" fill="#dc3545" radius={getRadius("90%", salesData.chartData[0])}>
                      <LabelList
                        dataKey="90%"
                        position="center"
                        formatter={(value) => `${Math.round(value)}%`}
                        fill="#fff"
                      />
                    </Bar>
                  )}
                  {salesData.chartData[0]["75%"] > 0 && (
                    <Bar dataKey="75%" stackId="a" fill="#0d6efd" radius={getRadius("75%", salesData.chartData[0])}>
                      <LabelList
                        dataKey="75%"
                        position="center"
                        formatter={(value) => `${Math.round(value)}%`}
                        fill="#fff"
                      />
                    </Bar>
                  )}
                  {salesData.chartData[0]["50%"] > 0 && (
                    <Bar dataKey="50%" stackId="a" fill="#ffc107" radius={getRadius("50%", salesData.chartData[0])}>
                      <LabelList
                        dataKey="50%"
                        position="center"
                        formatter={(value) => `${Math.round(value)}%`}
                        fill="#000"
                      />
                    </Bar>
                  )}
                  {salesData.chartData[0]["0%"] > 0 && (
                    <Bar dataKey="0%" stackId="a" fill="#000000" radius={getRadius("0%", salesData.chartData[0])}>
                      <LabelList
                        dataKey="0%"
                        position="center"
                        formatter={(value) => `${Math.round(value)}%`}
                        fill="#fff"
                      />
                    </Bar>
                  )}
                </>
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className={`row justify-content-center tabla-ventas-detalle-row${loadingData ? " d-none" : ""}`}>
        <div className="col-md-12">
          <table className="table table-bordered table-striped table-hover table-sm">
            <thead className="text-center align-middle">
              <tr>
                {modoComparar && <th style={{ width: "3%" }} />}
                {!ocultarIdCode && (
                  <th style={{ width: anchosTabla.id, cursor: "pointer" }} onClick={() => handleSort("id")}>
                    ID{" "}
                    {sortConfig.key === "id" && (
                      <span style={{ fontSize: "10px", opacity: 0.6 }}>
                        {sortConfig.direction === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </th>
                )}
                <th
                  style={{ width: oportunidadesHabilitadas ? anchosTabla.cliente : "16%", cursor: "pointer" }}
                  onClick={() => handleSort("cliente")}
                >
                  CLIENTE{" "}
                  {sortConfig.key === "cliente" && (
                    <span style={{ fontSize: "10px", opacity: 0.6 }}>{sortConfig.direction === "asc" ? "▲" : "▼"}</span>
                  )}
                </th>
                {/* Fase 1b (2026-10-04): OPORTUNIDAD y CAMPAÑA se unifican en una sola columna
                    (decisión de Ignacio: "una oportunidad es una campaña con varias cotizaciones,
                    de las que una termina cerrándose") — puramente de presentación, la lógica de
                    oportunidades (filtro, "Asignar a oportunidad", agrupación 10.272) no cambia. */}
                <th style={{ width: anchosTabla.campana, cursor: "pointer" }} onClick={() => handleSort("campana")}>
                  CAMPAÑA{" "}
                  {sortConfig.key === "campana" && (
                    <span style={{ fontSize: "10px", opacity: 0.6 }}>{sortConfig.direction === "asc" ? "▲" : "▼"}</span>
                  )}
                </th>
                <th
                  style={{ width: oportunidadesHabilitadas ? anchosTabla.cierre : "6%", cursor: "pointer" }}
                  onClick={() => handleSort("cierre")}
                >
                  CIERRE{" "}
                  {sortConfig.key === "cierre" && (
                    <span style={{ fontSize: "10px", opacity: 0.6 }}>{sortConfig.direction === "asc" ? "▲" : "▼"}</span>
                  )}
                </th>
                <th
                  style={{ width: oportunidadesHabilitadas ? anchosTabla.factura : "10%", cursor: "pointer" }}
                  onClick={() => handleSort("factura")}
                >
                  FACTURA{" "}
                  {sortConfig.key === "factura" && (
                    <span style={{ fontSize: "10px", opacity: 0.6 }}>{sortConfig.direction === "asc" ? "▲" : "▼"}</span>
                  )}
                </th>
                <th
                  style={{ width: oportunidadesHabilitadas ? anchosTabla.ticket : "8%", cursor: "pointer" }}
                  onClick={() => handleSort("ticket")}
                >
                  TICKET{" "}
                  {sortConfig.key === "ticket" && (
                    <span style={{ fontSize: "10px", opacity: 0.6 }}>{sortConfig.direction === "asc" ? "▲" : "▼"}</span>
                  )}
                </th>
                <th
                  style={{ width: oportunidadesHabilitadas ? anchosTabla.status : "6%", cursor: "pointer" }}
                  onClick={() => handleSort("status")}
                >
                  STATUS{" "}
                  {sortConfig.key === "status" && (
                    <span style={{ fontSize: "10px", opacity: 0.6 }}>{sortConfig.direction === "asc" ? "▲" : "▼"}</span>
                  )}
                </th>
                <th style={{ width: anchosTabla.om }}>OM</th>
                {!ocultarIdCode && (
                  <th style={{ width: oportunidadesHabilitadas ? anchosTabla.code : "6%", cursor: "pointer" }}>CODE</th>
                )}
                <th style={{ width: anchosTabla.tipo, cursor: "pointer" }} onClick={() => handleSort("tipo")}>
                  TIPO{" "}
                  {sortConfig.key === "tipo" && (
                    <span style={{ fontSize: "10px", opacity: 0.6 }}>{sortConfig.direction === "asc" ? "▲" : "▼"}</span>
                  )}
                </th>
                <th className="celda-inventario-ajustada" style={{ width: anchosTabla.inventario }}>
                  INVENTARIO
                </th>
                {!ocultarIntermediario && (
                  <th style={{ width: oportunidadesHabilitadas ? anchosTabla.intermediario : "11%" }}>
                    {/* Fase 1b (2026-10-04): header renombrado a AGENCIA (pedido de Ignacio, mismo
                        dato) — variable/anchos internos quedan "intermediario" a propósito, 10.14. */}
                    AGENCIA
                  </th>
                )}
                <th
                  style={{ width: oportunidadesHabilitadas ? anchosTabla.ejecutivo : "7%", cursor: "pointer" }}
                  onClick={() => handleSort("ejecutivo")}
                >
                  EJECUTIVO{" "}
                  {sortConfig.key === "ejecutivo" && (
                    <span style={{ fontSize: "10px", opacity: 0.6 }}>{sortConfig.direction === "asc" ? "▲" : "▼"}</span>
                  )}
                </th>
                {/* Un solo kebab (10.136): el header queda mínimo, sin "ACCIONES" ocupando ancho. */}
                <th style={{ width: anchosTabla.acciones }} />
              </tr>
            </thead>
            <tbody className="table-striped" style={{ height: "35px", lineHeight: "35px" }}>
              {filasConGrupo.length === 0 ? (
                <tr>
                  <td colSpan={totalColumnasTabla} className="text-center">
                    {modoComparar
                      ? "No hay clientes con 2+ propuestas al 50%, 75% o 90%"
                      : "No hay ventas ni oportunidades según los filtros seleccionados :("}
                  </td>
                </tr>
              ) : (
                filasConGrupo.map(({ fila: oportunidad, esPrimeraDelGrupo, rowSpanGrupo, grupoIndex }) => (
                  <Fragment key={`${oportunidad.type || "regular"}-${oportunidad.id}`}>
                    <tr
                      onClick={oportunidad.type === "contrato" ? () => toggleContrato(oportunidad.id) : undefined}
                      className={
                        oportunidadesHabilitadas
                          ? grupoIndex % 2 === 0
                            ? "fila-grupo-impar"
                            : "fila-grupo-par"
                          : undefined
                      }
                      style={{
                        height: "35px",
                        lineHeight: "35px",
                        cursor: oportunidad.type === "contrato" ? "pointer" : undefined,
                        backgroundColor:
                          modoComparar && selectedClientId && Number(oportunidad.client_id) !== selectedClientId
                            ? "#f8f9fa"
                            : undefined,
                        opacity:
                          modoComparar && selectedClientId && Number(oportunidad.client_id) !== selectedClientId
                            ? 0.45
                            : 1,
                      }}
                    >
                      {modoComparar && (
                        <td className="text-center align-middle">
                          <input
                            type="checkbox"
                            checked={isSelectedComparar(oportunidad)}
                            onChange={() => handleSelectComparar(oportunidad)}
                            disabled={
                              !isSelectedComparar(oportunidad) &&
                              seleccionComparar.length >= 4 &&
                              selectedClientId === Number(oportunidad.client_id)
                            }
                            style={{ cursor: "pointer", width: "16px", height: "16px" }}
                          />
                        </td>
                      )}
                      {(() => {
                        // ► de contratos (2026-09-30): vive en ID; si ID está oculto (<1440px) pasa
                        // a CLIENTE, antes del nombre — el toggle sigue siendo el click en la fila.
                        const flechaContrato = oportunidad.type === "contrato" && (
                          <span
                            style={{
                              marginRight: 4,
                              display: "inline-block",
                              transform: expandedContratos.has(oportunidad.id) ? "rotate(90deg)" : "rotate(0deg)",
                              transition: "transform 0.2s",
                            }}
                          >
                            ▶
                          </span>
                        );
                        // Acceso al detalle (2026-09-30): con CODE oculto, CLIENTE pasa a ser el
                        // link que hoy tiene CODE, misma condición (solo si hay code).
                        const clienteEsLink = ocultarIdCode && oportunidad.code && oportunidad.code !== "-";
                        return (
                          <>
                            {!ocultarIdCode && (
                              <td className="text-center align-middle">
                                {flechaContrato}
                                {oportunidad.type === "contrato" && oportunidad.display_id
                                  ? oportunidad.display_id
                                  : oportunidad.id}
                              </td>
                            )}
                            <td
                              className="text-start align-middle celda-una-linea"
                              title={oportunidad.cliente || undefined}
                            >
                              {ocultarIdCode && flechaContrato}
                              {clienteEsLink ? (
                                <span
                                  style={{ cursor: "pointer", color: "#007bff" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleShowDetails(oportunidad);
                                  }}
                                >
                                  {oportunidad.cliente}
                                </span>
                              ) : (
                                oportunidad.cliente
                              )}
                            </td>
                          </>
                        );
                      })()}
                      {esPrimeraDelGrupo && (
                        // CAMPAÑA fusiona OPORTUNIDAD (Fase 1b): si la fila tiene oportunidad con
                        // nombre, se muestra esa; si no, la campaña de la cotización — misma celda
                        // fusionada por rowSpan sobre su grupo que ya traía OPORTUNIDAD (4.16.9/
                        // 4.16.10, intacta: con la feature apagada todo grupo ya es de largo 1, el
                        // mismo render sirve para los dos casos). Filas 2+ del grupo NO renderizan
                        // esta celda — queda cubierta por el rowSpan.
                        <td
                          className="text-start align-middle celda-una-linea"
                          rowSpan={rowSpanGrupo}
                          title={(() => {
                            const nombre = nombreOportunidad(oportunidad);
                            if (!nombre) return oportunidad.campana || undefined;
                            return oportunidad.campana ? `${nombre} (campaña: ${oportunidad.campana})` : nombre;
                          })()}
                        >
                          {(() => {
                            const texto = nombreOportunidad(oportunidad) || oportunidad.campana;
                            if (!texto) return "-";
                            return (texto.length > 18 ? texto.slice(0, 18) + "..." : texto).toUpperCase();
                          })()}
                        </td>
                      )}
                      <td className="text-center align-middle">
                        {oportunidad.mes_cierre &&
                        typeof oportunidad.mes_cierre === "string" &&
                        /^\d{2}-\d{4}$/.test(oportunidad.mes_cierre)
                          ? `${mesEnLetras(oportunidad.mes_cierre.split("-")[0])}/${oportunidad.mes_cierre
                              .split("-")[1]
                              .slice(-2)}`
                          : "-"}
                      </td>
                      <td className="text-center align-middle">
                        {oportunidad.type === "contrato" ? (
                          <span>
                            {oportunidad.mes_inicio && mesEnLetras(oportunidad.mes_inicio.split("-")[0])}/
                            {oportunidad.mes_inicio?.split("-")[1]?.slice(-2)}
                            {" → "}
                            {oportunidad.mes_fin && mesEnLetras(oportunidad.mes_fin.split("-")[0])}/
                            {oportunidad.mes_fin?.split("-")[1]?.slice(-2)}
                          </span>
                        ) : oportunidad.facturafecha ? (
                          `${new Date(oportunidad.facturafecha)
                            .toLocaleString(localString(pais), { month: "short" })
                            .replace(/\.$/, "")
                            .toUpperCase()}/${new Date(oportunidad.facturafecha).getFullYear().toString().slice(-2)}`
                        ) : oportunidad.mes_factura &&
                          typeof oportunidad.mes_factura === "string" &&
                          /^\d{2}-\d{4}$/.test(oportunidad.mes_factura) ? (
                          `${mesEnLetras(oportunidad.mes_factura.split("-")[0])}/${oportunidad.mes_factura
                            .split("-")[1]
                            .slice(-2)}`
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="text-end align-middle">
                        {viewInUSD
                          ? formatMoneda(getImporteEnUSD(oportunidad, getDolarValue, fallbackRate), true)
                          : formatMoneda(
                              oportunidad.type === "btl" ? oportunidad.total_investment || 0 : oportunidad.importe || 0,
                              oportunidad.moneda_dolar
                            )}
                      </td>
                      <td
                        className={`text-center align-middle ${
                          oportunidad.estatus === "100%"
                            ? "bg-success text-white"
                            : oportunidad.estatus === "90%"
                              ? "bg-danger text-white"
                              : oportunidad.estatus === "75%"
                                ? "bg-primary text-white"
                                : oportunidad.estatus === "50%"
                                  ? "bg-warning text-dark"
                                  : "bg-black text-white"
                        }`}
                      >
                        {oportunidad.estatus}
                      </td>
                      <td className="text-center align-middle" onClick={(e) => e.stopPropagation()}>
                        {renderOmCelda(oportunidad)}
                      </td>
                      {!ocultarIdCode && (
                        <td className="text-center align-middle celda-una-linea" title={oportunidad.code || undefined}>
                          {oportunidad.code && oportunidad.code !== "-" ? (
                            <span
                              style={{ cursor: "pointer", color: "#007bff" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShowDetails(oportunidad);
                              }}
                            >
                              {oportunidad.code}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                      )}
                      <td className="text-center align-middle">{tipoVenta(oportunidad)}</td>
                      <td className="text-center align-middle celda-una-linea celda-inventario-ajustada">
                        {oportunidad.type === "btl"
                          ? inventarioBTL(oportunidad, !ocultarIdCode)
                          : oportunidad.type === "contrato"
                            ? INVENTARIO_CONTRATO
                            : inventarioVenta(oportunidad, !ocultarIdCode)}
                      </td>
                      {/* <td className="text-center align-middle">
                      {oportunidad.formatopauta === "A"
                        ? "5"
                        : oportunidad.formatopauta === "B"
                          ? "10"
                          : oportunidad.formatopauta === "C"
                            ? "20"
                            : oportunidad.formatopauta}
                    </td>
                    <td className="text-center align-middle">{oportunidad.periodopauta}</td> */}
                      {!ocultarIntermediario &&
                        (() => {
                          // Truncado a 18 + "..." (mismo criterio que OPORTUNIDAD/CAMPAÑA, 4.16.9):
                          // el nombre real del intermediario no tiene tope de largo (a diferencia
                          // de esas dos, que SÍ truncan en JS) — sin este tope, nombres reales como
                          // "Hivestack Technologies Inc." (27 car.) desbordan la celda. `celda-una-
                          // linea` es la red de seguridad final, nunca una 2ª línea.
                          const nombreIntermediario =
                            oportunidad.type === "btl"
                              ? "EDIFICIOS"
                              : (() => {
                                  const clientIntermediary = clienteIntermediarioMap[oportunidad.client_id];
                                  return clientIntermediary && intermediaryMap[clientIntermediary]
                                    ? intermediaryMap[clientIntermediary]
                                    : null;
                                })();
                          const nombreIntermediarioCorto = nombreAgenciaCorto(nombreIntermediario);
                          return (
                            <td
                              className="text-center align-middle celda-una-linea"
                              title={nombreIntermediario || undefined}
                            >
                              {nombreIntermediarioCorto
                                ? nombreIntermediarioCorto.length > 18
                                  ? `${nombreIntermediarioCorto.slice(0, 18)}...`
                                  : nombreIntermediarioCorto
                                : "-"}
                            </td>
                          );
                        })()}
                      <td
                        className="text-center align-middle celda-una-linea"
                        title={oportunidad.ejecutivo || undefined}
                      >
                        {oportunidad.ejecutivo}
                      </td>
                      {/* Un solo KebabMenu (10.136): cada ítem conserva EXACTAMENTE el gate que
                          tenía su ícono — el kebab es solo presentación, no relaja permisos.
                          stopPropagation: el click (y el menú, que sube por el árbol de React
                          aunque viva en un portal) no debe desplegar el acordeón del contrato. */}
                      <td className="text-center align-middle" onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const items = [];
                          if (oportunidad.type !== "btl" && puedeEditarOportunidad(oportunidad)) {
                            items.push({ label: "Edición rápida", onClick: () => handleFlashEdit(oportunidad) });
                          }
                          if (
                            oportunidad.type !== "btl" &&
                            oportunidad.type !== "contrato" &&
                            puedeEditarOportunidad(oportunidad)
                          ) {
                            items.push({ label: "Editar", onClick: () => handleEdit(oportunidad) });
                          }
                          if (
                            oportunidadesHabilitadas &&
                            oportunidad.type !== "btl" &&
                            oportunidad.oportunidad_id != null &&
                            ESTATUS_ABIERTOS_OPORTUNIDAD.includes(oportunidad.estatus) &&
                            puedeEditarOportunidad(oportunidad)
                          ) {
                            items.push({
                              label: "Asignar a oportunidad",
                              onClick: () => setAsignarOportunidadFila(oportunidad),
                            });
                          }
                          if (oportunidad.type !== "btl" && oportunidad.type !== "contrato") {
                            const omInfo = ordenesMedioVigentes.get(`sale:${oportunidad.id}`);
                            if (!omInfo && puedeCargarOmDelCliente(oportunidad.client_id)) {
                              items.push({
                                label: "Cargar OM",
                                onClick: () => setCargarOmData(construirCargarOmData(oportunidad)),
                              });
                            } else if (omInfo && esArchivoPendiente(omInfo.archivoPath)) {
                              if (puedeCargarOmDelCliente(oportunidad.client_id)) {
                                items.push({ label: "Adjuntar PDF", onClick: () => setAdjuntarPdfData(omInfo) });
                              }
                            } else if (omInfo) {
                              if (puedeVerOmDelCliente(oportunidad.client_id)) {
                                items.push({ label: "Ver OM", onClick: () => verOm(omInfo) });
                              }
                              if (puedeCargarOmDelCliente(oportunidad.client_id)) {
                                items.push({
                                  label: "Reemplazar OM",
                                  onClick: () => abrirReemplazarOm(omInfo, null),
                                });
                              }
                            }
                          }
                          if (puedeEditarOportunidad(oportunidad)) {
                            items.push({
                              label: "Eliminar",
                              danger: true,
                              onClick: () =>
                                handleConfirmationModal({
                                  id: oportunidad.type === "btl" ? oportunidad.full_id : oportunidad.id,
                                  type: oportunidad.type,
                                }),
                            });
                          }
                          return items.length > 0 ? (
                            <KebabMenu minWidth={200} buttonClassName={styles.kebabAccion} items={items} />
                          ) : (
                            "-"
                          );
                        })()}
                      </td>
                    </tr>
                    {oportunidad.type === "contrato" &&
                      expandedContratos.has(oportunidad.id) &&
                      oportunidad.meses &&
                      oportunidad.meses.map((mesObj, idx) => (
                        <tr
                          key={`contrato-${oportunidad.id}-mes-${idx}`}
                          style={{ backgroundColor: "rgba(0,0,0,0.02)" }}
                        >
                          {modoComparar && <td className="text-center align-middle">-</td>}
                          {!ocultarIdCode && <td className="text-center align-middle">-</td>}
                          <td className="text-center align-middle">-</td>
                          {/* Columna CAMPAÑA (fusiona OPORTUNIDAD, Fase 1b): sin td acá — la cubre
                              el rowSpan de la fila del grupo (ver bloque de arriba), nunca un
                              placeholder propio. */}
                          <td className="text-center align-middle">-</td>
                          <td className="text-center align-middle">
                            <span style={{ fontWeight: 500 }}>
                              {mesEnLetras(mesObj.mes.split("-")[0])}/{mesObj.mes.split("-")[1]?.slice(-2)}
                            </span>
                          </td>
                          <td className="text-end align-middle">
                            {formatMoneda(mesObj.importe, oportunidad.moneda_dolar)}
                          </td>
                          <td className="text-center align-middle">-</td>
                          <td className="text-center align-middle" onClick={(e) => e.stopPropagation()}>
                            {renderOmCelda(oportunidad, mesObj)}
                          </td>
                          {!ocultarIdCode && <td className="text-center align-middle">-</td>}
                          {/* TIPO: la cuota no tiene tipo propio (es del contrato, en la fila padre). */}
                          <td className="text-center align-middle">-</td>
                          <td className="text-center align-middle celda-una-linea celda-inventario-ajustada">
                            {(() => {
                              const facturada = !!mesObj.facturanumero;
                              const puedeAsignar = canAssignEdificios && !facturada;
                              const tieneEdif = tieneEdificiosAsignados(mesObj.edificios_ids);
                              const mostrarNumeros = facturada || tieneEdif;
                              if (!puedeAsignar && !mostrarNumeros) return "-";
                              const icono = puedeAsignar ? (
                                <FaBuilding
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEdificiosModalData({ contratoId: oportunidad.id, idx });
                                  }}
                                  title={tieneEdif ? "Modificar edificios asignados" : "Asignar edificios"}
                                  style={{ cursor: "pointer", color: tieneEdif ? "#198754" : "#dc3545" }}
                                />
                              ) : null;
                              // Sin asignación (y editable): solo el ícono, sin "-|-" al lado —
                              // "si la cuota ya tiene asignación, al lado van los números" implica
                              // lo contrario también: sin asignación, no hay números que mostrar.
                              if (!mostrarNumeros) {
                                return (
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      flex: "0 0 auto",
                                      width: INV_SLOT.icono,
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    {icono}
                                  </span>
                                );
                              }
                              return renderInventario(
                                mesObj.edificios_totales,
                                mesObj.pantallas_totales,
                                mesObj.personas_asignadas,
                                { conAudiencia: !ocultarIdCode, iconoSlot: icono }
                              );
                            })()}
                          </td>
                          {!ocultarIntermediario && <td className="text-center align-middle">-</td>}
                          <td className="text-center align-middle">-</td>
                          <td className="text-center align-middle" onClick={(e) => e.stopPropagation()}>
                            {(() => {
                              const cuotaItems = [];
                              if (
                                !mesObj.facturanumero &&
                                puedeEditarOportunidad({ ...oportunidad, facturanumero: null })
                              ) {
                                cuotaItems.push({
                                  label: "Edición rápida",
                                  onClick: () => handleFlashEdit({ ...oportunidad, facturanumero: null }),
                                });
                              }
                              const omInfoCuota = ordenesMedioVigentes.get(`contrato:${oportunidad.id}:${mesObj.mes}`);
                              if (!omInfoCuota && puedeCargarOmDelCliente(oportunidad.client_id)) {
                                cuotaItems.push({
                                  label: "Cargar OM",
                                  onClick: () => setCargarOmData(construirCargarOmData(oportunidad, mesObj)),
                                });
                              } else if (omInfoCuota && esArchivoPendiente(omInfoCuota.archivoPath)) {
                                if (puedeCargarOmDelCliente(oportunidad.client_id)) {
                                  cuotaItems.push({
                                    label: "Adjuntar PDF",
                                    onClick: () => setAdjuntarPdfData(omInfoCuota),
                                  });
                                }
                              } else if (omInfoCuota) {
                                if (puedeVerOmDelCliente(oportunidad.client_id)) {
                                  cuotaItems.push({ label: "Ver OM", onClick: () => verOm(omInfoCuota) });
                                }
                                if (puedeCargarOmDelCliente(oportunidad.client_id)) {
                                  cuotaItems.push({
                                    label: "Reemplazar OM",
                                    onClick: () => abrirReemplazarOm(omInfoCuota, oportunidad.id),
                                  });
                                }
                              }
                              return cuotaItems.length > 0 ? (
                                <KebabMenu minWidth={200} buttonClassName={styles.kebabAccion} items={cuotaItems} />
                              ) : (
                                "-"
                              );
                            })()}
                          </td>
                        </tr>
                      ))}
                  </Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Modal isOpen={isEditing} toggle={() => setIsEditing(false)} size="xl">
        <ModalHeader>Editar Oportunidad #{editingOpportunity?.id || "Cargando..."}</ModalHeader>
        <ModalBody>
          {editingOpportunity ? (
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmitEdit)}>
                <div className="row g-3">
                  <div className="col-4">
                    <label htmlFor="estatus" className="form-label">
                      Estatus
                    </label>
                    <select
                      className={`form-control ${errors.estatus ? "is-invalid" : ""}`}
                      id="estatus"
                      {...register("estatus", { required: true })}
                    >
                      <option value="">Selecciona un porcentaje</option>
                      <option value="100%">100%</option>
                      <option value="90%">90%</option>
                      <option value="75%">75%</option>
                      <option value="50%">50%</option>
                      <option value="0%">0%</option>
                    </select>
                    {errors.estatus && <div className="invalid-feedback">Este campo es obligatorio</div>}
                  </div>
                  <div className="col-4">
                    <label htmlFor="mes_cierre" className="form-label">
                      Mes de Cierre
                    </label>
                    <select
                      className={`form-control ${errors.mes_cierre ? "is-invalid" : ""}`}
                      id="flash_mes_cierre"
                      {...register("mes_cierre", { required: true })}
                    >
                      {months.map((month) => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                    {errors.mes_cierre && <div className="invalid-feedback">Este campo es obligatorio</div>}
                  </div>
                  <div className="col-4">
                    <label htmlFor="mes" className="form-label">
                      Mes Facturación
                    </label>
                    <select
                      className={`form-control ${errors.mes ? "is-invalid" : ""}`}
                      id="mes"
                      {...register("mes", { required: true })}
                      defaultValue={editingOpportunity?.mes || "01"}
                    >
                      {months.map((month) => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                    {errors.mes && <div className="invalid-feedback">Este campo es obligatorio</div>}
                  </div>
                </div>
              </form>
            </FormProvider>
          ) : (
            <div style={{ maxHeight: 160, overflow: "hidden" }}>
              <Loader />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsEditing(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button color="primary" onClick={handleSubmit(onSubmitEdit)} disabled={loading}>
            {loading ? "Guardando..." : "Guardar"}
          </Button>
        </ModalFooter>
      </Modal>
      <Modal isOpen={showConfirmationModal} toggle={() => setShowConfirmationModal(false)}>
        <ModalHeader>Confirmación</ModalHeader>
        <ModalBody>¿Está seguro de eliminar esta oportunidad?</ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowConfirmationModal(false)}>
            Cancelar
          </Button>
          <Button color="primary" onClick={() => handleDelete(deletedOpportunity.id, deletedOpportunity.type)}>
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>
      <Modal isOpen={showDetailsModal} toggle={() => setShowDetailsModal(false)} size="xl">
        <ModalHeader toggle={() => setShowDetailsModal(false)} style={{ padding: "10px 15px" }}>
          <div className="d-flex align-items-center w-100">
            <div>
              <span>Detalles de la cotización #{selectedOpportunity?.code}</span>
            </div>
          </div>
        </ModalHeader>
        <ModalBody>
          {selectedOpportunity ? (
            <div className="details-container">
              <div className="row">
                <div className="col-md-6">
                  <p>
                    <strong>Ejecutivo:</strong>{" "}
                    {users.find((user) => user.id.toString() === selectedOpportunity.ejecutivo.toString())?.nombre ||
                      selectedOpportunity.ejecutivo}
                  </p>
                  <p>
                    <strong>Cliente:</strong> {selectedOpportunity.cliente}
                  </p>
                  <p>
                    <strong>Importe:</strong>{" "}
                    {viewInUSD
                      ? formatMoneda(getImporteEnUSD(selectedOpportunity, getDolarValue, fallbackRate), true)
                      : formatMoneda(
                          selectedOpportunity.type === "btl"
                            ? selectedOpportunity.total_investment || 0
                            : selectedOpportunity.importe || 0,
                          selectedOpportunity.moneda_dolar
                        )}
                  </p>
                  <p>
                    <strong>Mes de Cierre:</strong>{" "}
                    {selectedOpportunity.mes_cierre ? mesEnLetras(selectedOpportunity.mes_cierre.split("-")[0]) : "-"}
                  </p>
                  <p>
                    <strong>Mes Facturación:</strong>{" "}
                    {selectedOpportunity.mes_factura ? mesEnLetras(selectedOpportunity.mes_factura.split("-")[0]) : "-"}
                  </p>
                  <p>
                    <strong>Intermediario:</strong>{" "}
                    {intermediaryMap[selectedOpportunity.intermediario] || selectedOpportunity.intermediario || "-"}
                  </p>
                  <p>
                    <strong>Frecuencia:</strong>{" "}
                    {selectedOpportunity.formatopauta === "A"
                      ? "5"
                      : selectedOpportunity.formatopauta === "B"
                        ? "10"
                        : selectedOpportunity.formatopauta === "C"
                          ? "20"
                          : selectedOpportunity.formatopauta}
                  </p>
                  <p>
                    <strong>Días:</strong> {selectedOpportunity.periodopauta}
                  </p>
                  <p>
                    <strong>Tipo:</strong>{" "}
                    {selectedOpportunity.tipo === null || selectedOpportunity.tipo === "d"
                      ? "D"
                      : selectedOpportunity.tipo === "p"
                        ? "P"
                        : selectedOpportunity.tipo === "m"
                          ? "M"
                          : selectedOpportunity.tipo || "-"}
                  </p>
                  <p>
                    <strong>Pantallas Totales:</strong> {selectedOpportunity.pantallastotales}
                  </p>
                  <p>
                    <strong>Edificios Totales:</strong> {selectedOpportunity.edificiostotales}
                  </p>
                  <div>
                    <strong
                      onClick={toggleBuildingsList}
                      style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                    >
                      Edificios participantes{" "}
                      <span style={{ marginLeft: "5px" }}>{isBuildingsExpanded ? "▼" : "▶"}</span>
                    </strong>
                    <Collapse isOpen={isBuildingsExpanded}>
                      <ul>
                        {selectedOpportunity?.edificiosparticipantes?.length > 0 && edificios.length > 0 ? (
                          [...new Set(selectedOpportunity.edificiosparticipantes.split(","))].map((id, index) => {
                            const edificio = edificios.find((ed) => ed.id.toString() === id.trim());
                            return edificio ? (
                              <li key={`${id}-${index}`}>{edificio.edificio}</li>
                            ) : (
                              <li key={`${id}-${index}`}>Edificio ID {id} (desconocido)</li>
                            );
                          })
                        ) : edificios.length === 0 ? (
                          <li>No se pudieron cargar los edificios</li>
                        ) : (
                          <li>-</li>
                        )}
                      </ul>
                    </Collapse>
                  </div>
                </div>
              </div>
              {selectedOpportunity.facturanumero && (
                <div className="mt-3">
                  <h6>Información de Facturación</h6>
                  <p>
                    <strong>Número de Factura:</strong> {selectedOpportunity.facturanumero}
                  </p>
                  <p>
                    <strong>Fecha de Factura:</strong>{" "}
                    {new Date(selectedOpportunity.facturafecha).toLocaleDateString(localString(pais))}
                  </p>
                  <p>
                    <strong>Fecha de Cobro:</strong>{" "}
                    {selectedOpportunity._cobroReal?.cobrada
                      ? new Date(selectedOpportunity._cobroReal.fecha).toLocaleDateString(localString(pais))
                      : "-"}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div style={{ maxHeight: 160, overflow: "hidden" }}>
              <Loader />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowDetailsModal(false)}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>
      <FlashEditModal
        isOpen={isFlashEditing}
        onClose={() => {
          setIsFlashEditing(false);
          setFlashEditingOpportunity(null);
        }}
        opportunity={flashEditingOpportunity}
        onSave={onSubmitFlashEdit}
        saving={flashSaving}
        hideFacturacion={flashEditingOpportunity?.type === "contrato"}
        intermediarios={intermediarios}
        clientes={clientes}
      />
      <ModalAsignarEdificios
        show={edificiosModalData != null}
        onHide={() => setEdificiosModalData(null)}
        onSave={handleSaveEdificiosCuota}
        pais={pais}
        personasComprometidas={edificiosCuotaCtx?.personasComprometidas || 0}
        edificiosIdsActuales={edificiosCuotaCtx?.edificiosIds || null}
        mesLabel={edificiosCuotaCtx?.mesLabel || ""}
      />
      <CargarOrdenMedioModal
        show={cargarOmData != null}
        onHide={() => setCargarOmData(null)}
        data={cargarOmData}
        onSaved={recargarOrdenesMedioVigentes}
      />
      <AdjuntarPdfOmModal
        show={adjuntarPdfData != null}
        onHide={() => setAdjuntarPdfData(null)}
        omInfo={adjuntarPdfData}
        onSaved={recargarOrdenesMedioVigentes}
      />
      {/* Botón flotante comparar */}
      {modoComparar && seleccionComparar.length >= 2 && (
        <button
          className="btn btn-dark"
          onClick={async () => {
            const codigos = seleccionComparar.map((op) => op.code).filter(Boolean);
            if (codigos.length > 0) {
              try {
                const cots = await getCotizacionesByCodigos(codigos);
                setCotizacionesComparar(cots);
              } catch {
                setCotizacionesComparar([]);
              }
            } else {
              setCotizacionesComparar([]);
            }
            setComparadorOpen(true);
          }}
          style={{
            position: "fixed",
            bottom: "24px",
            right: "24px",
            zIndex: 1050,
            borderRadius: "24px",
            padding: "10px 24px",
            fontWeight: 600,
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          }}
        >
          Comparar ({seleccionComparar.length})
        </button>
      )}
      <ComparadorModal
        isOpen={comparadorOpen}
        toggle={() => setComparadorOpen(false)}
        oportunidades={seleccionComparar}
        cotizaciones={cotizacionesComparar}
        pais={pais}
        viewInUSD={viewInUSD}
        getDolarValue={getDolarValue}
        fallbackRate={fallbackRate}
      />
      <AsignarOportunidadModal
        isOpen={!!asignarOportunidadFila}
        onClose={() => setAsignarOportunidadFila(null)}
        cotizacion={asignarOportunidadFila}
        nombreActual={asignarOportunidadFila ? nombreOportunidad(asignarOportunidadFila) : null}
        onAsignada={onOportunidadAsignada}
      />
    </div>
  );
};
